import React, { useState, useEffect, useMemo } from "react";
import { FONT_HEADING } from "../../../theme";
import {
  Autocomplete,
  createFilterOptions,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  FormControlLabel,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import SearchIcon from "@mui/icons-material/Search";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  createShopifyWebhook,
  getShopifyWebhooks,
  deleteShopifyWebhook,
} from "../../../APICalling/shopifyApi";
import { getShopifyStores } from "../../../APICalling/shopifyAdminApi";
import { sampleWebhookBody } from "../../../sample_json/sample_mapping/samplewebhook";
import SHOPIFY_FIELDS_MAP from "../../../config/shopifyFields";
import {
  getZohoModules,
  getZohoModuleFieldsViaApi,
} from "../../../APICalling/zohoAdminApi";
import {
  getSettingByType,
  upsertSetting,
  deleteSettingById,
} from "../../../APICalling/settingApi";
import {
  SHOPIFY_SHOP_URL_HEADER_KEY,
  buildShopifyShopUrlHeader,
  SHOPIFY_ORG_CONN,
  EXTENSION_IDENTIFIER,
} from "../../../config/serviceConstant";
import {
  getOpenSnakErrorData,
  getOpenSnakSuccessData,
} from "../../../config/otherConstant";
import WEBHOOK_LIST from "../../../sample_json/webhook_list.json";
import {
  DIRECTION_SHOPIFY_TO_CRM,
  DIRECTION_CRM_TO_SHOPIFY,
  CRM_TRIGGER_LIST,
  normaliseDirection,
} from "../../../config/syncDirectionConstant";
import { isShopifyCompatibleWithZoho } from "../../../util/typeCompat";

const ZOHO_APPS = [{ key: "zohocrm", label: "Zoho CRM" }];

// Target Shopify resource for CRM2Shopify mappings, inferred from the Zoho
// module. Shopify webhook topics encode their resource ("customers/create" →
// category "Customers"), but CRM workflow events don't — the outbound
// destination follows from which Zoho module the rule is for. Keys are Zoho
// module api_names; values are SHOPIFY_FIELDS_MAP keys.
const MODULE_TO_SHOPIFY_RESOURCE = {
  Contacts: "Customers",
  Accounts: "Customers",
  Leads: "Customers",
  Sales_Orders: "Orders",
  Deals: "Orders",
  Invoices: "Orders",
  Quotes: "Draft Orders",
  Products: "Products",
};

// ── Wizard Step Indicator ──
function StepIndicator({ currentStep, steps, maxReachable = 0, onStepClick }) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        px: 3.5,
        pt: 2.5,
        pb: 2,
        gap: 0,
      }}
    >
      {steps.map((step, i) => {
        const clickable = i <= maxReachable;
        return (
          <React.Fragment key={i}>
            <Box
              onClick={clickable ? () => onStepClick(i) : undefined}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                flexShrink: 0,
                cursor: clickable ? "pointer" : "default",
                "&:hover": clickable ? { opacity: 0.8 } : {},
              }}
            >
              <Box
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 13,
                  fontWeight: 600,
                  border: "1.5px solid",
                  borderColor:
                    i < currentStep
                      ? "#22c55e"
                      : i === currentStep
                      ? "#09090b"
                      : "#e4e4e7",
                  bgcolor:
                    i < currentStep
                      ? "#22c55e"
                      : i === currentStep
                      ? "#09090b"
                      : "#fff",
                  color:
                    i < currentStep
                      ? "#fff"
                      : i === currentStep
                      ? "#fff"
                      : "#71717a",
                  transition: "all 0.2s",
                }}
              >
                {i < currentStep ? "\u2713" : i + 1}
              </Box>
              <Typography
                sx={{
                  fontSize: 13,
                  fontWeight: i === currentStep ? 600 : 500,
                  color:
                    i < currentStep
                      ? "#16a34a"
                      : i === currentStep
                      ? "#09090b"
                      : "#71717a",
                  whiteSpace: "nowrap",
                }}
              >
                {step}
              </Typography>
            </Box>
            {i < steps.length - 1 && (
              <Box
                sx={{
                  flex: 1,
                  height: "1.5px",
                  bgcolor: i < currentStep ? "#22c55e" : "#e4e4e7",
                  mx: 1.25,
                  transition: "background 0.2s",
                }}
              />
            )}
          </React.Fragment>
        );
      })}
    </Box>
  );
}

// ── Mapping row helpers ──
// Canonical row shape:
//   { Shopify, Zoho, default, shopifyType, zohoType, locked }
// `shopifyType` / `zohoType` are captured at save time so the server-side
// sync function can coerce values without re-fetching field metadata.
// `locked` marks a row as extension-defined and non-editable — used for the
// Shopify-ID external field that ties Zoho records back to Shopify.
// Backwards compatible with the legacy tuple shape: [shopify, zoho] or [value, zoho, "field"|"default"]
const EMPTY_ROW = () => ({
  Shopify: "",
  Zoho: "",
  default: false,
  shopifyType: "",
  zohoType: "",
  lookupModuleApiName: "",
  // Subform / list pairing: when both sides resolve to subform↔array, the
  // user maps each sub-field in `subRows` (recursive use of the same row
  // shape minus locked/required semantics).
  subformModuleApiName: "",
  subRows: [],
  // Optional guidance shown as the Zoho-field placeholder while the row is
  // unmapped — used by seeded rows whose Zoho target varies per org (e.g. the
  // Sales Order line-item sub-fields) so the client knows what to map.
  hint: "",
  locked: false,
});

// Built-in line-item subforms of Zoho CRM's inventory-class modules (Quotes,
// Sales Orders, Invoices) whose sub-fields aren't returned
// by ZOHO.CRM.META.getFields. Each entry's sub-field list is the canonical
// shape Zoho's record APIs expect under e.g. `Product_Details`. Acts as a
// fallback when API-driven discovery yields nothing.
const HARDCODED_SUBFORM_SUBFIELDS = {
  Product_Details: [
    {
      api_name: "product",
      display_label: "Product",
      data_type: "lookup",
      lookup: { module: { api_name: "Products" } },
    },
    { api_name: "quantity", display_label: "Quantity", data_type: "double" },
    {
      api_name: "list_price",
      display_label: "List Price",
      data_type: "currency",
    },
    {
      api_name: "unit_price",
      display_label: "Unit Price",
      data_type: "currency",
    },
    { api_name: "Discount", display_label: "Discount", data_type: "currency" },
    { api_name: "total", display_label: "Total", data_type: "currency" },
    {
      api_name: "total_after_discount",
      display_label: "Total After Discount",
      data_type: "currency",
    },
    {
      api_name: "net_total",
      display_label: "Net Total",
      data_type: "currency",
    },
    { api_name: "Tax", display_label: "Tax", data_type: "double" },
    {
      api_name: "description",
      display_label: "Description",
      data_type: "textarea",
    },
    { api_name: "book", display_label: "Book", data_type: "text" },
    {
      api_name: "quantity_in_stock",
      display_label: "Quantity in Stock",
      data_type: "double",
    },
    {
      api_name: "product_description",
      display_label: "Product Description",
      data_type: "textarea",
    },
  ],
  // Sales Orders line-items subform — display label "Ordered Items", api_name
  // `Ordered_Items`, linking module `Ordered_Items`. Zoho surfaces its children
  // via the associated module (not inline on the parent field), so list the
  // mappable ones here as a fallback for when API discovery yields nothing —
  // otherwise the subform's Zoho-field dropdowns render empty. `Product_Name`
  // is a lookup to Products (the sync resolves it from Shopify's product_id).
  Ordered_Items: [
    {
      api_name: "Product_Name",
      display_label: "Product Name",
      data_type: "lookup",
      lookup: { module: { api_name: "Products" } },
    },
    { api_name: "Quantity", display_label: "Quantity", data_type: "double" },
    { api_name: "List_Price", display_label: "List Price", data_type: "currency" },
    { api_name: "Description", display_label: "Description", data_type: "textarea" },
    { api_name: "Discount", display_label: "Discount", data_type: "currency" },
    { api_name: "Tax", display_label: "Tax", data_type: "currency" },
  ],
  // The "Variant" subform is created on the Products module by this
  // extension. Zoho's getFields doesn't surface its sub-fields, so list
  // them here so the field-mapping UI can pair them with Shopify's
  // variants[].* sub-fields.
  Variant: [
    { api_name: "title", display_label: "Title", data_type: "text" },
    { api_name: "price", display_label: "Price", data_type: "currency" },
    { api_name: "compare_at_price", display_label: "Compare-at Price", data_type: "currency" },
    { api_name: "sku", display_label: "SKU", data_type: "text" },
    { api_name: "barcode", display_label: "Barcode", data_type: "text" },
    { api_name: "position", display_label: "Position", data_type: "integer" },
    { api_name: "inventory_quantity", display_label: "Inventory Quantity", data_type: "integer" },
    { api_name: "inventory_policy", display_label: "Inventory Policy", data_type: "picklist" },
    { api_name: "inventory_management", display_label: "Inventory Management", data_type: "text" },
    { api_name: "fulfillment_service", display_label: "Fulfillment Service", data_type: "text" },
    { api_name: "requires_shipping", display_label: "Requires Shipping", data_type: "boolean" },
    { api_name: "taxable", display_label: "Taxable", data_type: "boolean" },
    { api_name: "weight", display_label: "Weight", data_type: "double" },
    { api_name: "weight_unit", display_label: "Weight Unit", data_type: "text" },
    { api_name: "grams", display_label: "Grams", data_type: "integer" },
    { api_name: "option1", display_label: "Option 1", data_type: "text" },
    { api_name: "option2", display_label: "Option 2", data_type: "text" },
    { api_name: "option3", display_label: "Option 3", data_type: "text" },
    { api_name: "image_id", display_label: "Image ID", data_type: "bigint" },
    { api_name: "inventory_item_id", display_label: "Inventory Item ID", data_type: "bigint" },
    { api_name: "Shopify_Variant_ID", display_label: "Shopify Variant ID", data_type: "bigint" },
  ],
};

// True when a Zoho field metadata entry represents a subform-style container
// (custom subform, inventory-modules Product_Details list, or any other field
// we have hardcoded fallback sub-fields for).
const isSubformLikeZohoField = (zohoField) => {
  if (!zohoField) return false;
  const dt = zohoField?.data_type;
  if (dt === "subform" || dt === "list") return true;
  // Some Zoho responses return Product_Details with a non-standard data_type
  // (sometimes empty, sometimes "multilookup"). Fall back to api_name match.
  const apiName = zohoField.api_name || "";
  return Object.prototype.hasOwnProperty.call(
    HARDCODED_SUBFORM_SUBFIELDS,
    apiName
  );
};

// Substring filter that searches BOTH the human label and the api_name.
// MUI's default filter only stringifies via getOptionLabel (which returns the
// human label), so api_name matches like "Variant" won't surface for fields
// whose display_label is "Vendor Name". This filter fixes that.
const fieldFilter = createFilterOptions({
  ignoreCase: true,
  matchFrom: "any",
  trim: true,
  stringify: (opt) =>
    [
      opt?.display_label,
      opt?.field_label,
      opt?.api_name,
      typeof opt === "string" ? opt : "",
    ]
      .filter(Boolean)
      .join(" "),
});

// When zohoFields came from the new POST /shopify/getcrmfieids endpoint,
// subform sub-fields are already in the flat zohoFields list tagged with
// `_groupKey === parentApiName` and qualified api_names like
// "Variant.title". This helper extracts them and strips the qualifying
// prefix so the sub-row dropdown shows bare names ("title", "price", …).
// Returns [] when no fields are tagged with that group — caller should fall
// back to the subform cache / hardcoded fallback.
const getSubformSubFieldsFromGrouped = (zohoFields, parentApiName) => {
  if (!parentApiName || !Array.isArray(zohoFields)) return [];
  const prefix = `${parentApiName}.`;
  return zohoFields
    .filter((f) => f && f._groupKey === parentApiName)
    .map((f) => ({
      ...f,
      api_name:
        typeof f.api_name === "string" && f.api_name.startsWith(prefix)
          ? f.api_name.slice(prefix.length)
          : f.api_name,
      _group: undefined, // no nested grouping inside the sub-block
    }));
};

// Pull the subform sub-module API name and any inline sub-field metadata out
// of a Zoho field record. Zoho returns this in different shapes depending on
// whether the field is a custom subform or the inventory-line-items list. As
// a last resort, fall back to the hardcoded shape for well-known fields like
// Product_Details whose sub-fields aren't surfaced by getFields.
const getSubformContext = (zohoField) => {
  if (!isSubformLikeZohoField(zohoField)) return null;
  const moduleApiName =
    zohoField?.subform?.module?.api_name ||
    (typeof zohoField?.subform?.module === "string"
      ? zohoField.subform.module
      : "") ||
    zohoField?.subform_module ||
    "";
  const inlineFields = zohoField?.subform?.fields || zohoField?.fields || [];
  const fallbackFields = HARDCODED_SUBFORM_SUBFIELDS[zohoField.api_name] || [];
  return { moduleApiName, inlineFields, fallbackFields };
};

// Filter a flat Shopify field list down to the dotted sub-fields of the given
// array field. e.g. parentApiName "line_items" → returns "line_items[].title",
// "line_items[].quantity", … as defined in config/shopifyFields/*.json.
const filterShopifyArraySubFields = (shopifyFields, parentApiName) => {
  if (!parentApiName) return [];
  const prefix = `${parentApiName}[].`;
  return shopifyFields.filter((f) => (f.api_name || f).startsWith(prefix));
};

// A sub-row whose Shopify path is "<parent>[].id" tracks the source record's
// own identifier (e.g. line_items[].id). This must stay in the mapping so the
// sync function can reconcile new vs existing sub-records on update operations.
const isIdSubRow = (sr) =>
  typeof sr?.Shopify === "string" && /\[\]\.id$/.test(sr.Shopify);
// Zoho fields created by this extension are namespaced with the extension
// identifier prefix (e.g. `easyshopifyforzohocrm__Shopify_ID_EXT`). Any row
// pointing at such a field is considered owned by the extension and must
// not be edited or removed by the user.
const isExtensionOwnedZohoField = (apiName) =>
  typeof apiName === "string" &&
  apiName.startsWith(`${EXTENSION_IDENTIFIER}__`);

// Standard Zoho fields we always treat as fixed/locked defaults (e.g. Pipeline
// on Deals gets a locked "Standard (Standard)" default value). Use this check
// in addition to `isExtensionOwnedZohoField` wherever we decide if a row is
// non-editable.
const ALWAYS_LOCKED_DEFAULT_FIELDS = new Set(["Pipeline"]);
const isAlwaysLockedDefault = (apiName) =>
  ALWAYS_LOCKED_DEFAULT_FIELDS.has(apiName);

// Extension-owned Zoho field that stores the Shopify shop URL. Injected as a
// locked default-value row in every mapping so downstream sync logic can
// disambiguate records by shop without the user needing to configure it.
const SHOPIFY_SHOP_URL_FIELD = `${EXTENSION_IDENTIFIER}__Shopify_Shop_URL`;
// Standard Zoho Deals field — injected only when the Zoho CRM Deals module
// is targeted, so every auto-created deal lands in the default pipeline.
const PIPELINE_FIELD = "Pipeline";
const PIPELINE_DEFAULT_VALUE = "Standard (Standard)";

/**
 * Ensure a single locked default-value row exists in `rows` for `fieldApiName`.
 * If a row already exists for this field, update its Shopify value; otherwise
 * append a new locked row. Skips if the field isn't present in `zohoFields`.
 */
const upsertLockedDefaultRow = (
  rows,
  fieldApiName,
  shopifyValue,
  zohoFields
) => {
  const fieldExists = zohoFields?.some(
    (f) => (f.api_name || f) === fieldApiName
  );
  if (!fieldExists) return rows;

  const newRow = {
    ...EMPTY_ROW(),
    Zoho: fieldApiName,
    Shopify: shopifyValue || "",
    default: true,
    locked: true,
    shopifyType: "",
    zohoType: "text",
  };

  const existingIdx = rows.findIndex((r) => r.Zoho === fieldApiName);
  if (existingIdx >= 0) {
    const next = [...rows];
    next[existingIdx] = { ...next[existingIdx], ...newRow };
    return next;
  }

  // Insert before any trailing EMPTY_ROW so the "add more" slot stays last
  const lastIdx = rows.length - 1;
  const lastIsEmpty =
    rows.length > 0 && !rows[lastIdx].Zoho && !rows[lastIdx].Shopify;
  if (lastIsEmpty) {
    return [...rows.slice(0, lastIdx), newRow, rows[lastIdx]];
  }
  return [...rows, newRow];
};

/**
 * Ensure every mapping carries the extension's required locked rows:
 *   - Shopify Shop URL (all modules)
 *   - Pipeline (Zoho CRM Deals module only — ties orders to a shop-specific pipeline)
 *
 * Idempotent; preserves row ordering; updates existing locked rows to reflect
 * the currently-selected shop.
 */
const withShopUrlRow = (rows, shopName, zohoFields, context = {}) => {
  let next = upsertLockedDefaultRow(
    rows,
    SHOPIFY_SHOP_URL_FIELD,
    shopName,
    zohoFields
  );
  if (context.zohoApp === "Zoho CRM" && context.moduleName === "Deals") {
    next = upsertLockedDefaultRow(
      next,
      PIPELINE_FIELD,
      PIPELINE_DEFAULT_VALUE,
      zohoFields
    );
  }
  return next;
};

const normaliseRow = (r) => {
  if (!r) return EMPTY_ROW();
  if (Array.isArray(r)) {
    const zoho = r[1] ?? "";
    return {
      Shopify: r[0] ?? "",
      Zoho: zoho,
      default: r[2] === "default" || r[2] === true,
      shopifyType: "",
      zohoType: "",
      lookupModuleApiName: "",
      subformModuleApiName: "",
      subRows: [],
      hint: "",
      locked: isExtensionOwnedZohoField(zoho),
    };
  }
  const zoho = r.Zoho ?? r.zoho ?? "";
  const rawSub = r.subRows ?? r.sub_rows ?? [];
  return {
    Shopify: r.Shopify ?? r.shopify ?? "",
    Zoho: zoho,
    default: r.default === true,
    shopifyType: r.shopifyType ?? r.shopify_type ?? "",
    zohoType: r.zohoType ?? r.zoho_type ?? "",
    lookupModuleApiName:
      r.lookupModuleApiName ?? r.lookup_module_api_name ?? "",
    subformModuleApiName:
      r.subformModuleApiName ?? r.subform_module_api_name ?? "",
    subRows: Array.isArray(rawSub) ? rawSub.map(normaliseRow) : [],
    hint: r.hint ?? "",
    locked:
      r.locked === true ||
      isExtensionOwnedZohoField(zoho) ||
      isAlwaysLockedDefault(zoho),
  };
};

// ── Default-value editor (renders type-aware input based on Zoho field metadata) ──
function DefaultValueEditor({ value, zohoField, onChange }) {
  const dataType = zohoField?.data_type;
  const picklist = zohoField?.pick_list_values;

  // Picklist / multi-select picklist → Autocomplete from pick_list_values
  if (
    (dataType === "picklist" || dataType === "multiselectpicklist") &&
    Array.isArray(picklist) &&
    picklist.length > 0
  ) {
    const options = picklist.map(
      (p) => p?.actual_value ?? p?.display_value ?? p
    );
    return (
      <Autocomplete
        size="small"
        fullWidth
        options={options}
        getOptionLabel={(opt) => {
          const match = picklist.find(
            (p) => (p?.actual_value ?? p?.display_value) === opt
          );
          return match?.display_value ?? String(opt ?? "");
        }}
        value={options.includes(value) ? value : null}
        onChange={(_, newVal) => onChange(newVal || "")}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder="— Select default —"
            sx={{ "& .MuiInputBase-input": { fontSize: 14 } }}
          />
        )}
      />
    );
  }

  // Boolean → true/false select
  if (dataType === "boolean") {
    return (
      <Autocomplete
        size="small"
        fullWidth
        options={["true", "false"]}
        value={value === "true" || value === "false" ? value : null}
        onChange={(_, newVal) => onChange(newVal || "")}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder="— Select —"
            sx={{ "& .MuiInputBase-input": { fontSize: 14 } }}
          />
        )}
      />
    );
  }

  // Date / datetime → native picker
  if (dataType === "date" || dataType === "datetime") {
    return (
      <TextField
        size="small"
        fullWidth
        type={dataType === "datetime" ? "datetime-local" : "date"}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        InputLabelProps={{ shrink: true }}
        sx={{ "& .MuiInputBase-input": { fontSize: 14 } }}
      />
    );
  }

  // Numeric → number input
  if (
    dataType === "integer" ||
    dataType === "double" ||
    dataType === "currency" ||
    dataType === "bigint"
  ) {
    return (
      <TextField
        size="small"
        fullWidth
        type="number"
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Enter default value"
        sx={{ "& .MuiInputBase-input": { fontSize: 14 } }}
      />
    );
  }

  // Fallback: plain text
  return (
    <TextField
      size="small"
      fullWidth
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      placeholder="Enter default value"
      sx={{ "& .MuiInputBase-input": { fontSize: 14 } }}
    />
  );
}

// ── Field Mapping Row ──
function MappingRow({
  row,
  index,
  shopifyFields,
  zohoFields,
  allRows,
  onChange,
  onRemove,
  onAdd,
  onSourceChange,
  isRequired,
  isLocked,
  // When true, a field already used by another row in this group is NOT hidden
  // from the dropdown — so the same Zoho/Shopify field can be mapped more than
  // once. Enabled for subform sub-rows (e.g. a lookup that legitimately needs
  // reuse); left off for top-level rows where duplicates aren't allowed.
  allowDuplicates = false,
  // Data-flow direction indicator. Default (false) renders "Zoho ← Shopify"
  // (Shopify2CRM). CRM2Shopify mappings flip the arrow to "Zoho → Shopify" —
  // same row shape, the backend interprets by settingSchema.direction.
  reverse = false,
}) {
  const isDefault = row.default === true;
  const lockOutlineSx = isLocked
    ? {
        "& .MuiOutlinedInput-notchedOutline": { borderColor: "#a1a1aa" },
        bgcolor: "#f4f4f5",
      }
    : {};

  // Dedup only against rows in the same source mode (field-mode rows live in a different namespace from default-mode literals)
  const selectedShopify = allRows
    .map((r, i) => (i !== index && r.default !== true ? r.Shopify : null))
    .filter(Boolean);
  const selectedZoho = allRows
    .map((r, i) => (i !== index ? r.Zoho : null))
    .filter(Boolean);

  const selectedZohoField = zohoFields.find(
    (f) => (f.api_name || f) === row.Zoho
  );
  const selectedShopifyField = shopifyFields.find(
    (f) => (f.api_name || f) === row.Shopify
  );

  // Narrow the opposite-side dropdown to fields whose type can be coerced into
  // the already-picked side. We also always keep the currently-selected option
  // in the list so legacy/incompatible rows don't disappear from the UI.
  const availableShopify = shopifyFields.filter((f) => {
    const apiName = f.api_name || f;
    if (!allowDuplicates && selectedShopify.includes(apiName)) return false;
    if (apiName === row.Shopify) return true;
    if (isDefault) return true;
    if (!selectedZohoField) return true;
    // Subform-like Zoho fields (custom subforms, Product_Details, …) only
    // accept an array Shopify field. Detection is by data_type AND
    // hardcoded api_name, so it works even when Zoho returns a non-standard
    // data_type (which is what happens with Product_Details on some orgs).
    if (isSubformLikeZohoField(selectedZohoField)) {
      return f.type === "array";
    }
    return isShopifyCompatibleWithZoho(
      f.type,
      selectedZohoField.data_type,
      selectedZohoField.api_name || selectedZohoField,
      f.api_name || f
    );
  });
  const availableZoho = zohoFields.filter((f) => {
    const apiName = f.api_name || f;
    if (!allowDuplicates && selectedZoho.includes(apiName)) return false;
    if (apiName === row.Zoho) return true;
    if (isDefault || !selectedShopifyField) return true;
    // Inverse rule: Shopify array fields only pair with subform-like Zoho.
    if (selectedShopifyField.type === "array") {
      return isSubformLikeZohoField(f);
    }
    return isShopifyCompatibleWithZoho(
      selectedShopifyField.type,
      f.data_type,
      apiName,
      selectedShopifyField.api_name || selectedShopifyField
    );
  });

  // A hinted, still-unmapped row (e.g. a seeded Sales Order line-item sub-field
  // whose Zoho target varies per org). Surface the hint in the placeholder and
  // flag it with a dashed amber outline so the client knows to complete it.
  const needsMapping = !isLocked && !row.Zoho && !!row.hint;

  // Group the Zoho options module-wise. The fields endpoint returns the main
  // module's own fields PLUS cross-module fields reachable via lookups, so
  // identically-labelled options (e.g. three "Billing Street"s — this module's,
  // the Contact's, the Account's) are indistinguishable without their module.
  // Each field carries `_group` (module display name) and `_isMainModule`;
  // sort main-module fields first, then the other groups alphabetically —
  // MUI's groupBy needs same-group options adjacent or it repeats headers.
  // Sub-row fields carry no `_group` and render ungrouped as before.
  const groupedZoho = [...availableZoho].sort((a, b) => {
    const aRank = a?._isMainModule === false ? 1 : 0;
    const bRank = b?._isMainModule === false ? 1 : 0;
    if (aRank !== bRank) return aRank - bRank;
    return String(a?._group || "").localeCompare(String(b?._group || ""));
  });

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "1fr 72px 28px 1fr 64px",
        alignItems: "center",
        py: "7px",
        borderBottom: "1px solid #f4f4f5",
      }}
    >
      <Autocomplete
        size="small"
        fullWidth
        disabled={isLocked}
        options={groupedZoho}
        filterOptions={fieldFilter}
        getOptionLabel={(opt) => {
          const label = opt?.display_label || opt?.field_label || opt || "";
          // Cross-module fields keep their module in the closed-input label so
          // a picked "Billing Street" stays identifiable after the dropdown
          // closes (the dropdown itself shows the module as a group header).
          return opt?._isMainModule === false && opt?._group
            ? `${label} — ${opt._group}`
            : label;
        }}
        groupBy={(opt) => opt?._group || ""}
        renderGroup={(params) =>
          params.group ? (
            <li key={params.key}>
              <Box
                sx={{
                  position: "sticky",
                  top: "-8px",
                  zIndex: 1,
                  px: 1.25,
                  py: "5px",
                  bgcolor: "#f8fafc",
                  borderBottom: "1px solid #eef2f7",
                  fontSize: 12,
                  fontWeight: 700,
                  color: "#4338ca",
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                }}
              >
                {params.group}
              </Box>
              <Box component="ul" sx={{ p: 0, m: 0 }}>
                {params.children}
              </Box>
            </li>
          ) : (
            // Ungrouped options (sub-rows, legacy flat lists) — no header.
            <li key={params.key}>{params.children}</li>
          )
        }
        renderOption={(props, opt) => (
          <li {...props}>
            <Box sx={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
              <Typography sx={{ fontSize: 14, color: "#09090b" }}>
                {opt?.display_label || opt?.field_label || opt || ""}
              </Typography>
              {opt?.api_name && (
                <Typography sx={{ fontSize: 12, color: "#a1a1aa" }} noWrap>
                  {opt.api_name}
                </Typography>
              )}
            </Box>
          </li>
        )}
        value={zohoFields.find((f) => (f.api_name || f) === row.Zoho) || null}
        onChange={(_, newVal) =>
          onChange(index, "Zoho", newVal ? newVal.api_name || newVal : "")
        }
        disableClearable={isRequired || isLocked}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={needsMapping ? row.hint : "— Zoho field —"}
            title={needsMapping ? row.hint : undefined}
            sx={{
              "& .MuiInputBase-input": {
                fontSize: 14,
                ...(needsMapping
                  ? { "&::placeholder": { color: "#b45309", opacity: 1, fontStyle: "italic" } }
                  : {}),
              },
              ...(isRequired && !isLocked
                ? {
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#f59e0b",
                    },
                  }
                : {}),
              ...(needsMapping
                ? {
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#f59e0b",
                      borderStyle: "dashed",
                    },
                  }
                : {}),
              ...lockOutlineSx,
            }}
          />
        )}
        isOptionEqualToValue={(opt, val) =>
          (opt.api_name || opt) === (val.api_name || val)
        }
      />
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          px: "6px",
        }}
      >
        {isDefault ? (
          <Typography sx={{ fontSize: 15, fontWeight: 700, color: "#a1a1aa" }}>
            =
          </Typography>
        ) : reverse ? (
          <ArrowForwardIcon sx={{ fontSize: 17, color: "#a1a1aa" }} />
        ) : (
          <ArrowBackIcon sx={{ fontSize: 17, color: "#a1a1aa" }} />
        )}
      </Box>
      <IconButton
        size="small"
        disabled={isLocked}
        onClick={() => onSourceChange(index, !isDefault)}
        title={
          isLocked
            ? "This mapping is defined by the extension and cannot be changed"
            : isDefault
            ? "Switch to Shopify field"
            : "Switch to default value"
        }
        sx={{
          width: 26,
          height: 26,
          border: "1.5px solid",
          borderColor: isLocked ? "#e4e4e7" : isDefault ? "#f59e0b" : "#e4e4e7",
          color: isLocked ? "#a1a1aa" : isDefault ? "#f59e0b" : "#71717a",
          fontSize: 14,
        }}
      >
        {isLocked
          ? "\uD83D\uDD12"
          : isDefault
          ? "\uD83D\uDCCC"
          : "\uD83D\uDD17"}
      </IconButton>
      {isDefault ? (
        <DefaultValueEditor
          value={row.Shopify}
          zohoField={selectedZohoField}
          onChange={(newVal) => onChange(index, "Shopify", newVal)}
        />
      ) : (
        <Autocomplete
          size="small"
          fullWidth
          disabled={isLocked}
          options={availableShopify}
          filterOptions={fieldFilter}
          getOptionLabel={(opt) =>
            opt?.display_label || opt?.field_label || opt || ""
          }
          value={
            shopifyFields.find((f) => (f.api_name || f) === row.Shopify) || null
          }
          onChange={(_, newVal) =>
            onChange(index, "Shopify", newVal ? newVal.api_name || newVal : "")
          }
          disableClearable={isLocked}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="— Shopify field —"
              title={
                selectedShopifyField?._fullLabel ||
                selectedShopifyField?.display_label ||
                undefined
              }
              sx={{
                "& .MuiInputBase-input": { fontSize: 14 },
                ...lockOutlineSx,
              }}
            />
          )}
          isOptionEqualToValue={(opt, val) =>
            (opt.api_name || opt) === (val.api_name || val)
          }
        />
      )}
      <Box
        sx={{ display: "flex", justifyContent: "flex-end", gap: "6px", pl: 1 }}
      >
        <IconButton
          size="small"
          onClick={onAdd}
          disabled={isLocked}
          sx={{
            border: "1.5px solid",
            borderColor: isLocked ? "#e4e4e7" : "#22c55e",
            color: isLocked ? "#e4e4e7" : "#22c55e",
            width: 26,
            height: 26,
          }}
        >
          <AddIcon sx={{ fontSize: 16 }} />
        </IconButton>
        <IconButton
          size="small"
          onClick={() => onRemove(index)}
          disabled={isRequired || isLocked}
          sx={{
            border: "1.5px solid",
            borderColor: isRequired || isLocked ? "#e4e4e7" : "#ef4444",
            color: isRequired || isLocked ? "#e4e4e7" : "#ef4444",
            width: 26,
            height: 26,
          }}
        >
          <RemoveIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Box>
    </Box>
  );
}

// ── Subform sub-mapping block ──
// Renders a nested mapping table directly below a parent row whose Zoho side
// is a subform/list and whose Shopify side is an array. Each sub-row maps a
// Zoho subform sub-field (e.g. Product_Name, Quantity) to a Shopify array
// dotted sub-field (e.g. line_items[].title, line_items[].quantity).
function SubformBlock({
  parentRow,
  parentIndex,
  shopifyFields,
  subZohoFields,
  subformLoading,
  onSubRowChange,
  onSubRowAdd,
  onSubRowRemove,
  // Arrow direction for the nested rows — see MappingRow's `reverse`.
  reverse = false,
}) {
  // The subform block header already states "← LINE_ITEMS", so the full
  // "Line Items . <field>" label on each sub-row is redundant and gets clipped
  // in the indented block. Show just the leaf ("Product Id", "Quantity", …) —
  // symmetric with the Zoho side (which already strips its parent prefix) — and
  // keep the full dotted path as a hover title.
  const subShopifyFields = useMemo(() => {
    const prefix = `${parentRow.Shopify}[].`;
    return filterShopifyArraySubFields(shopifyFields, parentRow.Shopify).map(
      (f) => {
        const apiName = f?.api_name || f;
        const leaf =
          typeof apiName === "string" && apiName.startsWith(prefix)
            ? apiName.slice(prefix.length)
            : apiName;
        const shortLabel = String(leaf)
          .replace(/[_.]/g, " ")
          .replace(/\b\w/g, (c) => c.toUpperCase());
        const base = typeof f === "string" ? { api_name: f } : { ...f };
        return {
          ...base,
          display_label: shortLabel,
          _fullLabel: base.display_label || base.field_label || apiName,
        };
      }
    );
  }, [shopifyFields, parentRow.Shopify]);
  const subRows = parentRow.subRows?.length ? parentRow.subRows : [EMPTY_ROW()];

  return (
    <Box
      sx={{
        ml: 4,
        mr: 1,
        mb: 1,
        mt: "2px",
        borderLeft: "2px solid #c7d2fe",
        bgcolor: "#fafbff",
        borderRadius: "6px",
        px: 1.25,
        py: 1,
      }}
    >
      <Typography
        sx={{
          fontSize: 12,
          fontWeight: 600,
          color: "#4338ca",
          textTransform: "uppercase",
          letterSpacing: "0.04em",
          mb: 0.75,
          display: "flex",
          alignItems: "center",
          gap: "6px",
        }}
      >
        <Box component="span" sx={{ fontSize: 13 }}>
          ↳
        </Box>
        Subform fields ({parentRow.Zoho || "?"} → {parentRow.Shopify || "?"})
      </Typography>
      {subformLoading ? (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            py: 1.5,
            justifyContent: "center",
          }}
        >
          <CircularProgress size={14} />
          <Typography sx={{ fontSize: 13, color: "#71717a" }}>
            Loading subform fields…
          </Typography>
        </Box>
      ) : !parentRow.Zoho ? (
        <Typography sx={{ fontSize: 13, color: "#a1a1aa", py: 1 }}>
          Pick a Zoho subform field on the left to enable sub-field mapping.
        </Typography>
      ) : !parentRow.Shopify ? (
        <Typography sx={{ fontSize: 13, color: "#a1a1aa", py: 1 }}>
          Pick a Shopify array field on the right to enable sub-field mapping.
        </Typography>
      ) : subZohoFields.length === 0 || subShopifyFields.length === 0 ? (
        <Typography sx={{ fontSize: 13, color: "#a1a1aa", py: 1 }}>
          {subZohoFields.length === 0
            ? `No subform sub-fields available for "${parentRow.Zoho}".`
            : `No array sub-fields available under "${parentRow.Shopify}". Pick a Shopify field whose type is an array.`}
        </Typography>
      ) : (
        <>
          {subRows.some((sr) => sr.Shopify && !sr.Zoho) && (
            <Box
              sx={{
                display: "flex",
                alignItems: "flex-start",
                gap: "6px",
                bgcolor: "#fffbeb",
                border: "1px solid #fde68a",
                borderRadius: "6px",
                px: "10px",
                py: "7px",
                mb: 1,
                fontSize: 13,
                lineHeight: 1.5,
                color: "#92400e",
              }}
            >
              <Box component="span" sx={{ fontSize: 14, mt: "-1px" }}>
                &#9888;
              </Box>
              <Box>
                <strong>Finish these line-item mappings to enable order automation.</strong>{" "}
                The Shopify data is pre-filled on the right — pick the matching field
                from your&nbsp;
                <Box component="span" sx={{ fontFamily: "monospace" }}>
                  {parentRow.Zoho}
                </Box>{" "}
                subform on the left. These fields differ between CRM setups, so they're
                left blank until you confirm them.
              </Box>
            </Box>
          )}
          {subRows.map((sr, j) => (
            <MappingRow
              key={j}
              row={sr}
              index={j}
              shopifyFields={subShopifyFields}
              zohoFields={subZohoFields}
              allRows={subRows}
              onChange={(idx, key, val) =>
                onSubRowChange(parentIndex, idx, key, val)
              }
              onSourceChange={() => {}}
              onRemove={(idx) => onSubRowRemove(parentIndex, idx)}
              onAdd={() => onSubRowAdd(parentIndex)}
              isRequired={isIdSubRow(sr)}
              isLocked={false}
              allowDuplicates
              reverse={reverse}
            />
          ))}
        </>
      )}
    </Box>
  );
}

// ── Table helpers ──
const SERVICE_CHIP_COLORS = {
  "Zoho CRM": { bg: "#eff6ff", color: "#1d4ed8" },
};

// Colored chip palette for the record-action column. Each action gets a
// distinct hue + outline so users can scan a long list and immediately spot
// destructive (delete) or insertive (create) rows.
const ACTION_CHIP_COLORS = {
  create: { bg: "#f0fdf4", color: "#15803d", border: "#86efac" },
  upsert: { bg: "#eff6ff", color: "#1d4ed8", border: "#93c5fd" },
  update: { bg: "#fefce8", color: "#a16207", border: "#fde68a" },
  delete: { bg: "#fef2f2", color: "#b91c1c", border: "#fca5a5" },
};

const compareValues = (a, b) => {
  if (a == null && b == null) return 0;
  if (a == null) return -1;
  if (b == null) return 1;
  if (typeof a === "number" && typeof b === "number") return a - b;
  return String(a).toLowerCase().localeCompare(String(b).toLowerCase());
};

const TABLE_COLUMNS = [
  { key: "enabled", label: "Status", sortable: true, width: 80 },
  { key: "trigger", label: "Trigger", sortable: true },
  { key: "module", label: "Module", sortable: true },
  { key: "service", label: "Service", sortable: true, width: 140 },
  { key: "shop", label: "Shop", sortable: true },
  {
    key: "fieldCount",
    label: "Fields",
    sortable: true,
    width: 80,
    align: "right",
  },
  { key: "recordAction", label: "Action", sortable: true, width: 100 },
  { key: "actions", label: "", sortable: false, width: 100, align: "right" },
];

// ── Main FieldMapping Component ──
export default function FieldMapping({
  orgId,
  apiKey,
  zApiKey,
  dataCenterUrl,
  dataCenter,
  orgVarMaps,
  setOrgVarMaps,
  shop,
  setSnack,
  openMappingRequest,
  // When true, only the wizard Dialog is rendered (no list view / filters /
  // header). Used by the grouped view so Add/Edit open in-place instead of
  // switching tabs.
  embedded = false,
  // Fires after the wizard closes — embedding parents use it to refresh
  // their own derived view of configs.
  onWizardClose,
}) {
  const [modalOpen, setModalOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [editingId, setEditingId] = useState(null);
  // Tracks which openMappingRequest nonces we've already consumed so the
  // wizard only auto-opens once per request from the grouped view.
  const [consumedOpenNonce, setConsumedOpenNonce] = useState(null);

  // Configs are loaded from / persisted to settings API
  const [configs, setConfigs] = useState([]);
  const [configsLoading, setConfigsLoading] = useState(false);
  const [savingMapping, setSavingMapping] = useState(false);

  // List view: sort + filter state
  const [sortBy, setSortBy] = useState("trigger");
  const [sortDir, setSortDir] = useState("asc");
  const [searchText, setSearchText] = useState("");
  const [serviceFilter, setServiceFilter] = useState("all");
  const [moduleFilter, setModuleFilter] = useState("all");
  const [shopFilter, setShopFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");

  const handleSort = (columnKey) => {
    if (sortBy === columnKey) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(columnKey);
      setSortDir("asc");
    }
  };

  // Auth bundle for settingApi
  const settingAuth = { orgId, apiKey, dataCenterUrl, shop };

  // Map a raw setting row from the API into the local config shape.
  // Tolerant of camelCase / snake_case and stringified settingSchema.
  const mapSettingToConfig = (s) => {
    if (!s) return null;
    let schema = s.settingSchema ?? s.setting_schema ?? s.schema ?? {};
    if (typeof schema === "string") {
      try {
        schema = JSON.parse(schema);
      } catch {
        schema = {};
      }
    }
    const id = s.settingId ?? s.setting_id ?? s.id ?? "";
    const moduleApiName =
      s.moduleApiName ?? s.module_api_name ?? schema?.module ?? "";
    return {
      id,
      ...schema,
      module: moduleApiName || schema?.module || "",
    };
  };

  // Re-fetch saved field mappings (settingType: "systemDefined") from settings API
  const refreshConfigs = async () => {
    setConfigsLoading(true);
    const list = await getSettingByType(settingAuth, "systemDefined");
    const mapped = (list || []).map(mapSettingToConfig).filter(Boolean);
    setConfigs(mapped);
    setConfigsLoading(false);
    return mapped;
  };

  // Initial fetch of saved field mappings on mount / auth change
  useEffect(() => {
    if (!orgId || !apiKey || !dataCenterUrl) return;
    let cancelled = false;
    (async () => {
      setConfigsLoading(true);
      const list = await getSettingByType(settingAuth, "systemDefined");
      if (cancelled) return;
      const mapped = (list || []).map(mapSettingToConfig).filter(Boolean);
      setConfigs(mapped);
      setConfigsLoading(false);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId, apiKey, dataCenterUrl, shop]);

  // Step 1: Shopify
  const [shopName, setShopName] = useState("");
  const [triggerEvent, setTriggerEvent] = useState("");
  const [stores, setStores] = useState([]);
  const [storesLoading, setStoresLoading] = useState(false);
  // Sync direction of the mapping being edited. Shopify2CRM rules are
  // triggered by Shopify webhooks; CRM2Shopify rules by Zoho CRM workflows
  // (no webhook is registered for them). Locked once a mapping exists —
  // changing direction would silently re-route an existing rule.
  const [direction, setDirection] = useState(DIRECTION_SHOPIFY_TO_CRM);
  const isCrmToShopify = direction === DIRECTION_CRM_TO_SHOPIFY;
  // Trigger catalogue depends on direction: Shopify webhook topics vs Zoho
  // CRM workflow events.
  const TRIGGER_OPTIONS = isCrmToShopify ? CRM_TRIGGER_LIST : WEBHOOK_LIST;

  // Step 2: Zoho App — pinned to "Zoho CRM"; the picker is read-only and the
  // reset/edit paths keep this default. Other Zoho services (Books/Inventory)
  // aren't surfaced to merchants right now.
  const [zohoApp, setZohoApp] = useState("Zoho CRM");
  const [moduleName, setModuleName] = useState("");
  const [zohoModules, setZohoModules] = useState([]);
  const [modulesLoading, setModulesLoading] = useState(false);

  // Step 3: Field Mapping
  const [shopifyFields, setShopifyFields] = useState([]);
  const [zohoFields, setZohoFields] = useState([]);
  const [fieldsLoading, setFieldsLoading] = useState(false);
  const [mappingRows, setMappingRows] = useState([EMPTY_ROW()]);
  // Lazy cache of subform sub-fields keyed by parent Zoho field api_name.
  // Value shape: { fields: ZohoField[], loading: boolean }.
  const [subformFieldsCache, setSubformFieldsCache] = useState({});

  // Top-level mapping rows should only see *parent* Shopify fields — the
  // dotted "line_items[].title"-style sub-fields belong inside the nested
  // SubformBlock and pollute the parent dropdown if shown there. The full
  // `shopifyFields` list (with sub-fields) is still passed into SubformBlock
  // so it can extract them via `filterShopifyArraySubFields`.
  const topLevelShopifyFields = useMemo(
    () => shopifyFields.filter((f) => !(f.api_name || f || "").includes("[].")),
    [shopifyFields]
  );

  // Step 4: Actions
  const [recordAction, setRecordAction] = useState("create");
  const [upsertField, setUpsertField] = useState("");

  // Delete actions always match by the extension-owned Shopify ID field
  // (`easyshopifyforzohocrm__Shopify_ID_EXT` — Zoho CRM is the only service).
  const getShopifyIdFieldForService = (service) =>
    service === "Zoho CRM" ? `${EXTENSION_IDENTIFIER}__Shopify_ID_EXT` : "";
  const shopifyIdFieldForCurrent = getShopifyIdFieldForService(zohoApp);

  // When the user picks the Delete action, force the match field to the
  // Shopify-ID field so the sync function always targets the correct record.
  useEffect(() => {
    if (recordAction === "delete" && shopifyIdFieldForCurrent) {
      setUpsertField(shopifyIdFieldForCurrent);
    }
  }, [recordAction, shopifyIdFieldForCurrent]);

  const shopifyHeaders = {
    orgid: orgId,
    connname: EXTENSION_IDENTIFIER,
    apikey: apiKey,
    datacenterurl: dataCenterUrl,
    datacenter: dataCenter,
    [SHOPIFY_SHOP_URL_HEADER_KEY]: buildShopifyShopUrlHeader(shopName || shop),
  };

  // Fetch Shopify stores when modal opens
  useEffect(() => {
    if (modalOpen && stores.length === 0) {
      fetchStores();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalOpen]);

  const fetchStores = async () => {
    setStoresLoading(true);
    const tokensHeaders = {
      orgid: orgId,
      connname: SHOPIFY_ORG_CONN,
      apikey: apiKey,
      datacenterurl: dataCenterUrl,
      datacenter: dataCenter,
    };
    const data = await getShopifyStores(tokensHeaders);
    setStores(data);
    setStoresLoading(false);
  };

  // Fetch Zoho modules when step 2 is reached and app is selected
  useEffect(() => {
    if (currentStep === 1 && zohoApp && zohoModules.length === 0) {
      fetchZohoModules();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep, zohoApp]);

  // Fetch fields when step 3 is reached
  useEffect(() => {
    if (currentStep === 2 && moduleName && triggerEvent) {
      fetchFields();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep, moduleName, triggerEvent]);

  // Keep the Shopify Shop URL row's default value in sync with the currently-
  // selected shop (handles shop change after step 3 has loaded). Pipeline's
  // default value is fixed — don't touch it here.
  useEffect(() => {
    if (!zohoFields?.length) return;
    setMappingRows((prev) => {
      const idx = prev.findIndex((r) => r.Zoho === SHOPIFY_SHOP_URL_FIELD);
      if (idx < 0) return prev;
      if (prev[idx].Shopify === (shopName || "")) return prev;
      const next = [...prev];
      next[idx] = { ...next[idx], Shopify: shopName || "" };
      return next;
    });
  }, [shopName, zohoFields]);

  const fetchZohoModules = async () => {
    setModulesLoading(true);
    const modules = await getZohoModules(window.ZOHO);
    setZohoModules(modules);
    setModulesLoading(false);
  };

  // Changing the Zoho module invalidates the whole field mapping — the
  // previous module's fields (and any auto-populated / saved rows) don't apply
  // to the new module. Reset the rows + fetched field metadata so Step 3
  // reconstructs defaults (required fields + locked Shop URL / Pipeline rows)
  // for the newly-selected module. No-op when the value is unchanged, so it
  // never wipes rows that were just loaded for editing the same module.
  const handleModuleChange = (newModule) => {
    const next = newModule || "";
    if (next === moduleName) return;
    setModuleName(next);
    setMappingRows([EMPTY_ROW()]);
    setZohoFields([]);
    setSubformFieldsCache({});
    setUpsertField("");
  };

  const fetchFields = async () => {
    setFieldsLoading(true);
    // Look up Shopify fields from the local map. Shopify2CRM: the webhook
    // topic's category names the resource. CRM2Shopify: CRM workflow events
    // carry no Shopify resource — infer the outbound target from the Zoho
    // module instead (Contacts → Customers, Sales_Orders → Orders, …).
    const category = isCrmToShopify
      ? MODULE_TO_SHOPIFY_RESOURCE[moduleName] || ""
      : WEBHOOK_LIST.find((w) => w.value === triggerEvent)?.category || "";
    const sFields = SHOPIFY_FIELDS_MAP[category] || [];
    const zFields = await getZohoModuleFieldsViaApi(shopifyHeaders, moduleName);
    setShopifyFields(sFields);
    setZohoFields(zFields);

    // Auto-populate rows for required (system_mandatory) Zoho fields. For
    // required subform parents (e.g. Sales Orders → Ordered_Items), also
    // pre-fill the nested block with rows for each required subform sub-
    // field so the user only has to pick the Shopify side. The platform-
    // managed `Parent_Id` link is skipped — Zoho fills it automatically
    // and forcing the user to map it would just trip the half-filled
    // sub-row validation.
    const SUBFORM_AUTO_MANAGED = new Set(["Parent_Id"]);
    // Zoho-required fields we deliberately skip from auto-populate. Either
    // because Zoho fills them itself (system audit), they have no obvious
    // Shopify counterpart, or the user prefers to map them manually when
    // needed. Removing a field here means it stops appearing as a pre-
    // filled row even if Zoho's metadata flags it as `system_mandatory`.
    const AUTO_POPULATE_EXCLUDE = new Set([]);
    // Only auto-populate the TARGET module's own required fields. The
    // getcrmfieids endpoint returns a flat list that also includes required
    // fields reachable through cross-module lookups (Account → Account_Name,
    // Quote → Quoted_Items, Contact → First/Last Name, Deal → Stage/Pipeline,
    // Owner → Role/Profile, …), each tagged `_isMainModule: false`. Without
    // this guard the mapping would pre-fill dozens of unrelated rows (and
    // duplicate names) from every related module instead of just the module
    // the user picked. `!== false` keeps legacy/untagged fields (treated as
    // main-module) working.
    const requiredFields = zFields.filter(
      (f) =>
        f.system_mandatory &&
        f._isMainModule !== false &&
        !AUTO_POPULATE_EXCLUDE.has(f.api_name)
    );
    let nextRows = mappingRows;
    if (
      requiredFields.length > 0 &&
      mappingRows.length <= 1 &&
      !mappingRows[0]?.Zoho
    ) {
      const requiredRows = requiredFields.map((f) => {
        const row = { ...EMPTY_ROW(), Zoho: f.api_name };
        if (isSubformLikeZohoField(f)) {
          const ctx = getSubformContext(f);
          row.subformModuleApiName = ctx?.moduleApiName || "";
          const subFields = ctx?.inlineFields?.length
            ? ctx.inlineFields
            : ctx?.fallbackFields || [];
          const requiredSub = subFields.filter(
            (s) => s.system_mandatory && !SUBFORM_AUTO_MANAGED.has(s.api_name)
          );
          if (requiredSub.length > 0) {
            row.subRows = requiredSub.map((s) => ({
              ...EMPTY_ROW(),
              Zoho: s.api_name,
            }));
          }
        }
        return row;
      });
      // Add an empty row at the end for user to add more
      nextRows = [...requiredRows, EMPTY_ROW()];
    }

    // Always inject the extension's locked default-value rows (Shopify Shop URL,
    // Pipeline for Deals). They persist across edits and track shopName changes.
    nextRows = withShopUrlRow(nextRows, shopName, zFields, {
      zohoApp,
      moduleName,
    });

    setMappingRows(nextRows);

    // Pre-warm subform sub-field caches for any existing subform rows loaded
    // from a saved config. Without this, the nested block would render empty
    // until the user re-selects the parent Zoho field.
    nextRows.forEach((r) => {
      const zf = zFields.find((f) => (f.api_name || f) === r.Zoho);
      if (isSubformLikeZohoField(zf)) ensureSubformFieldsLoaded(zf);
    });

    setFieldsLoading(false);
  };

  const openModal = (configId = null, requestedDirection = null) => {
    setEditingId(configId);
    if (configId !== null) {
      const cfg = configs.find((c) => c.id === configId);
      if (cfg) {
        setShopName(cfg.shop);
        setTriggerEvent(cfg.trigger);
        // Legacy settings predate the direction field — they're webhook-driven.
        setDirection(normaliseDirection(cfg.direction));
        // Pin to "Zoho CRM" — even if a legacy mapping persisted Books/Inventory,
        // the picker is read-only so we override with the supported app.
        setZohoApp("Zoho CRM");
        setModuleName(cfg.module);
        // Normalise loaded rows (handles both legacy tuple and new object shape)
        setMappingRows(
          cfg.rows?.length ? cfg.rows.map(normaliseRow) : [EMPTY_ROW()]
        );
        setRecordAction(cfg.recordAction || "create");
        setUpsertField(cfg.upsertField || "");
        setCurrentStep(2); // jump to field mapping for edits
      }
    } else {
      setShopName("");
      setTriggerEvent("");
      setDirection(normaliseDirection(requestedDirection));
      setZohoApp("Zoho CRM");
      setModuleName("");
      setMappingRows([EMPTY_ROW()]);
      setRecordAction("create");
      setUpsertField("");
      setCurrentStep(0);
    }
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    onWizardClose?.();
  };

  // Auto-open the wizard when the grouped view requests it (add or edit).
  // Edit requests wait until the matching config is present in state so the
  // pre-fill doesn't silently fall back to a blank "add" form.
  useEffect(() => {
    if (!openMappingRequest) return;
    if (consumedOpenNonce === openMappingRequest.nonce) return;
    const { id, nonce, direction: reqDirection } = openMappingRequest;
    if (id == null) {
      openModal(null, reqDirection);
      setConsumedOpenNonce(nonce);
    } else if (configs.find((c) => c.id === id)) {
      openModal(id);
      setConsumedOpenNonce(nonce);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openMappingRequest, configs]);

  const nextStep = () => {
    if (currentStep === 0 && (!shopName || !triggerEvent)) {
      setSnack(getOpenSnakErrorData("Please select shop and trigger event."));
      return;
    }
    if (currentStep === 0 && !isCrmToShopify) {
      // Shopify webhook topics encode their module (customers/create → one
      // Contacts rule per shop), so a topic may exist once per shop per
      // direction. CRM workflow events are per-MODULE — their uniqueness is
      // checked at step 2, once the module is known.
      const duplicate = configs.find(
        (c) =>
          c.shop === shopName &&
          c.trigger === triggerEvent &&
          normaliseDirection(c.direction) === direction &&
          c.id !== editingId
      );
      if (duplicate) {
        setSnack(
          getOpenSnakErrorData(
            "This trigger event already exists for the selected store and direction."
          )
        );
        return;
      }
    }
    if (currentStep === 1 && (!zohoApp || !moduleName)) {
      setSnack(getOpenSnakErrorData("Please select Zoho app and module."));
      return;
    }
    if (currentStep === 1 && isCrmToShopify) {
      // CRM events are unique per shop + module + direction.
      const duplicate = configs.find(
        (c) =>
          c.shop === shopName &&
          c.trigger === triggerEvent &&
          c.module === moduleName &&
          normaliseDirection(c.direction) === direction &&
          c.id !== editingId
      );
      if (duplicate) {
        setSnack(
          getOpenSnakErrorData(
            `A "${triggerEvent}" CRM to Shopify mapping already exists for ${moduleName} on this store.`
          )
        );
        return;
      }
    }
    if (currentStep === 2) {
      const incomplete = mappingRows.some((r) => {
        if (!r.Shopify || !r.Zoho) return true;
        if (r.subRows && r.subRows.length > 0) {
          // A subform row with no completed sub-rows is fine — treat it as
          // "parent only" (server-side will skip nested mapping). But any
          // half-filled sub-row is an error.
          return r.subRows.some(
            (s) => (s.Shopify || s.Zoho) && (!s.Shopify || !s.Zoho)
          );
        }
        return false;
      });
      if (incomplete) {
        setSnack(
          getOpenSnakErrorData(
            "Please map both Shopify and Zoho fields for every row."
          )
        );
        return;
      }
      // Strict type-compat backstop: dropdown filtering is reactive, but
      // legacy configs / race conditions can still leave an incompatible
      // pair in state. Block step transition with a specific error so the
      // user knows which row to fix.
      for (let i = 0; i < mappingRows.length; i++) {
        const r = mappingRows[i];
        if (r.default || r.locked) continue;
        const zf = zohoFields.find((f) => (f.api_name || f) === r.Zoho);
        const sf = shopifyFields.find((f) => (f.api_name || f) === r.Shopify);
        if (zf && sf && !isPairCompatible(zf, sf)) {
          setSnack(
            getOpenSnakErrorData(
              `Row ${i + 1}: Zoho "${r.Zoho}" (${
                zf.data_type || "?"
              }) is not compatible with Shopify "${r.Shopify}" (${
                sf.type || "?"
              }).`
            )
          );
          return;
        }
        // Sub-rows: same matrix, looked up against the grouped zohoFields
        // (new POST endpoint shape) with cache as fallback for legacy paths.
        const groupedSubFieldsForRow = getSubformSubFieldsFromGrouped(
          zohoFields,
          r.Zoho
        );
        const subZohoFields =
          groupedSubFieldsForRow.length > 0
            ? groupedSubFieldsForRow
            : subformFieldsCache[r.Zoho]?.fields || [];
        const subShopifyFields = filterShopifyArraySubFields(
          shopifyFields,
          r.Shopify
        );
        for (let j = 0; j < (r.subRows || []).length; j++) {
          const s = r.subRows[j];
          if (!s.Shopify || !s.Zoho) continue;
          const szf = subZohoFields.find((f) => (f.api_name || f) === s.Zoho);
          const ssf = subShopifyFields.find(
            (f) => (f.api_name || f) === s.Shopify
          );
          if (szf && ssf && !isPairCompatible(szf, ssf)) {
            setSnack(
              getOpenSnakErrorData(
                `Row ${i + 1} sub-row ${j + 1}: Zoho "${s.Zoho}" (${
                  szf.data_type || "?"
                }) is not compatible with Shopify "${s.Shopify}" (${
                  ssf.type || "?"
                }).`
              )
            );
            return;
          }
        }
      }
    }
    if (currentStep < 3) setCurrentStep(currentStep + 1);
    else saveMapping();
  };

  const prevStep = () => {
    if (currentStep > 0) setCurrentStep(currentStep - 1);
  };

  const saveMapping = async () => {
    // Keep only fully-filled rows, then stamp each with the current Shopify
    // JSON type and Zoho data_type so the server-side sync can coerce values.
    // Default-value rows carry the user-entered literal in `Shopify`, so we
    // skip the Shopify-field lookup for those.
    const validRows = mappingRows
      .filter((r) => r.Shopify && r.Zoho)
      .map((r) => {
        const zohoField = zohoFields.find((f) => (f.api_name || f) === r.Zoho);
        const shopifyField = r.default
          ? null
          : shopifyFields.find((f) => (f.api_name || f) === r.Shopify);
        // Canonicalise zohoType: some orgs return Product_Details (and other
        // built-in subforms) with non-standard data_type values like "text"
        // or empty. The server-side sync needs the canonical "subform"
        // marker to know to emit a nested array, so we override here.
        let zohoType = zohoField?.data_type || r.zohoType || "";
        if (isSubformLikeZohoField(zohoField)) zohoType = "subform";
        const lookupModuleApiName =
          zohoType === "lookup"
            ? zohoField?.lookup?.module?.api_name ||
              zohoField?.lookup?.module ||
              r.lookupModuleApiName ||
              ""
            : "";

        // For subform/list parent rows, stamp each sub-row's types using the
        // cached subform sub-field metadata + the dotted Shopify sub-fields.
        // Empty / half-filled sub-rows are dropped (validation catches the
        // half-filled case earlier).
        let subRows = [];
        if (
          isSubformLikeZohoField(zohoField) &&
          shopifyField?.type === "array"
        ) {
          const groupedSub = getSubformSubFieldsFromGrouped(
            zohoFields,
            zohoField.api_name
          );
          const subZohoFields =
            groupedSub.length > 0
              ? groupedSub
              : subformFieldsCache[zohoField.api_name]?.fields || [];
          const subShopifyFields = filterShopifyArraySubFields(
            shopifyFields,
            shopifyField.api_name
          );
          subRows = (r.subRows || [])
            .filter((s) => s.Shopify && s.Zoho)
            .map((s) => {
              const sZ = subZohoFields.find(
                (f) => (f.api_name || f) === s.Zoho
              );
              const sS = subShopifyFields.find(
                (f) => (f.api_name || f) === s.Shopify
              );
              let subZohoType = sZ?.data_type || s.zohoType || "";
              if (isSubformLikeZohoField(sZ)) subZohoType = "subform";
              const subLookup =
                subZohoType === "lookup"
                  ? sZ?.lookup?.module?.api_name ||
                    sZ?.lookup?.module ||
                    s.lookupModuleApiName ||
                    ""
                  : "";
              return {
                ...s,
                zohoType: subZohoType,
                shopifyType: sS?.type || s.shopifyType || "",
                lookupModuleApiName: subLookup,
                subformModuleApiName: "",
                subRows: [],
              };
            });
        }

        return {
          ...r,
          zohoType,
          lookupModuleApiName,
          shopifyType: r.default
            ? ""
            : shopifyField?.type || r.shopifyType || "",
          subformModuleApiName: r.subformModuleApiName || "",
          subRows,
        };
      });

    // Preserve existing enabled state when editing
    const existing = editingId ? configs.find((c) => c.id === editingId) : null;
    const enabled = existing ? existing.enabled !== false : true;

    const triggerApiName =
      TRIGGER_OPTIONS.find((w) => w.value === triggerEvent)?.apiName || "";

    const settingSchema = {
      shop: shopName,
      trigger: triggerEvent,
      triggerApiName,
      service: zohoApp,
      module: moduleName,
      rows: validRows,
      recordAction,
      upsertField: recordAction === "create" ? "" : upsertField,
      // Routes the setting to the right sync path: Shopify webhook handlers
      // read Shopify2CRM settings, Zoho→Shopify workflow functions read
      // CRM2Shopify settings.
      direction,
      enabled,
    };

    setSavingMapping(true);
    const result = await upsertSetting(settingAuth, {
      settingId: editingId || "",
      settingSchema,
      moduleApiName: moduleName,
      settingType: "systemDefined",
    });
    setSavingMapping(false);

    if (!result) {
      setSnack(getOpenSnakErrorData("Failed to save field mapping."));
      return;
    }

    // Create Shopify webhook for new Shopify2CRM mappings — skip if one
    // already exists. CRM2Shopify mappings are fired by Zoho CRM workflows,
    // NOT Shopify webhooks, so nothing is registered for them.
    if (!editingId && !isCrmToShopify) {
      const savedSettingId =
        result?.settingId ?? result?.setting_id ?? result?.id ?? "";
      if (savedSettingId) {
        try {
          const existingWebhooks =
            (await getShopifyWebhooks(shopifyHeaders)) || [];
          const alreadyExists = existingWebhooks.some((wh) =>
            (wh.address || "").includes(`setting_id=${savedSettingId}`)
          );
          if (!alreadyExists) {
            const shopForWebhook = shopName || shop;
            const whBody = {
              // dataCenterUrl routes the callback to the merchant's own Zoho
              // DC (e.g. www.zohoapis.ca for Canada orgs).
              ...sampleWebhookBody(
                savedSettingId,
                zApiKey,
                triggerEvent,
                shopForWebhook,
                dataCenterUrl
              ),
              topic: triggerEvent,
            };
            await createShopifyWebhook(shopifyHeaders, whBody);
          }
        } catch (err) {
          console.error("[FieldMapping] Webhook creation error:", err);
        }
      }
    }

    // Optimistically merge the saved row into local state so the UI updates
    // immediately, even before the re-fetch resolves.
    const savedConfig = mapSettingToConfig(result) || {
      id: editingId || result?.settingId || result?.id || `tmp-${Date.now()}`,
      ...settingSchema,
      module: moduleName,
    };
    setConfigs((prev) => {
      const idx = prev.findIndex((c) => c.id === savedConfig.id);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = { ...next[idx], ...savedConfig };
        return next;
      }
      return [...prev, savedConfig];
    });

    // Then re-fetch from server to reconcile (e.g., to get the real settingId for new rows)
    refreshConfigs();
    closeModal();
    setSnack(getOpenSnakSuccessData("Field mapping saved!"));
  };

  const deleteConfig = async (id) => {
    if (!id) return;

    // Delete the corresponding Shopify webhook first (before the setting is
    // removed). CRM2Shopify rules never register a webhook — skip for them.
    const cfgToDelete = configs.find((c) => c.id === id);
    if (normaliseDirection(cfgToDelete?.direction) === DIRECTION_SHOPIFY_TO_CRM) {
      try {
        const webhooks = await getShopifyWebhooks(shopifyHeaders);
        const list = Array.isArray(webhooks) ? webhooks : [];
        const match = list.find((wh) => {
          const addr = wh.address || "";
          if (addr.includes("setting_id=" + id)) return true;
          if (wh.setting_id === id) return true;
          return false;
        });
        if (match?.id) {
          await deleteShopifyWebhook(shopifyHeaders, match.id, id);
        }
      } catch (e) {
        console.error("Failed to delete associated webhook:", e);
      }
    }

    // Then delete the setting
    const ok = await deleteSettingById(settingAuth, id);
    if (!ok) {
      setSnack(getOpenSnakErrorData("Failed to delete field mapping."));
      return;
    }

    await refreshConfigs();
    setSnack(getOpenSnakSuccessData("Field mapping deleted."));
  };

  const toggleConfig = async (id, val) => {
    const cfg = configs.find((c) => c.id === id);
    if (!cfg) return;
    // Optimistic update
    setConfigs(configs.map((c) => (c.id === id ? { ...c, enabled: val } : c)));
    const triggerApiName =
      cfg.triggerApiName ||
      WEBHOOK_LIST.find((w) => w.value === cfg.trigger)?.apiName ||
      "";
    const result = await upsertSetting(settingAuth, {
      settingId: id,
      settingSchema: {
        shop: cfg.shop,
        trigger: cfg.trigger,
        triggerApiName,
        service: cfg.service,
        module: cfg.module,
        rows: cfg.rows || [],
        recordAction: cfg.recordAction || "create",
        upsertField: cfg.upsertField || "",
        // Preserve the sync direction across enable/disable round-trips.
        direction: normaliseDirection(cfg.direction),
        enabled: val,
      },
      moduleApiName: cfg.module,
      settingType: "systemDefined",
    });
    if (!result) {
      // Revert on failure
      setConfigs(
        configs.map((c) => (c.id === id ? { ...c, enabled: !val } : c))
      );
      setSnack(getOpenSnakErrorData("Failed to update mapping status."));
    }
  };

  // Lazily load Zoho subform sub-fields the first time a subform/list field
  // is selected on the parent row. Inline sub-fields (returned with the
  // parent metadata) are used directly; otherwise we fetch via the subform's
  // own module api_name. Cached per Zoho parent field so repeated selections
  // don't re-hit the network.
  const ensureSubformFieldsLoaded = async (parentZohoField) => {
    const ctx = getSubformContext(parentZohoField);
    if (!ctx) return;
    const key = parentZohoField.api_name || "";
    if (!key) return;
    if (subformFieldsCache[key]) return;
    if (ctx.inlineFields?.length) {
      setSubformFieldsCache((prev) => ({
        ...prev,
        [key]: { fields: ctx.inlineFields, loading: false },
      }));
      return;
    }
    if (!ctx.moduleApiName) {
      // No sub-module to hit — use the hardcoded fallback if we have one
      // (e.g. Product_Details), otherwise mark the cache with an empty list
      // so the UI shows the "no sub-fields available" message.
      setSubformFieldsCache((prev) => ({
        ...prev,
        [key]: { fields: ctx.fallbackFields || [], loading: false },
      }));
      return;
    }
    setSubformFieldsCache((prev) => ({
      ...prev,
      [key]: { fields: [], loading: true },
    }));
    const fields = await getZohoModuleFieldsViaApi(
      shopifyHeaders,
      ctx.moduleApiName
    );
    const resolved =
      fields && fields.length ? fields : ctx.fallbackFields || [];
    setSubformFieldsCache((prev) => ({
      ...prev,
      [key]: { fields: resolved, loading: false },
    }));
  };

  // Strict pair-compatibility check used both reactively (clear an
  // incompatible same-side selection when the opposite side changes) and
  // at save time as a backstop. Mirrors the dropdown filter rules exactly.
  const isPairCompatible = (zohoField, shopifyField) => {
    if (!zohoField || !shopifyField) return true;
    if (isSubformLikeZohoField(zohoField)) return shopifyField.type === "array";
    if (shopifyField.type === "array") return isSubformLikeZohoField(zohoField);
    return isShopifyCompatibleWithZoho(
      shopifyField.type,
      zohoField.data_type,
      zohoField.api_name || zohoField,
      shopifyField.api_name || shopifyField
    );
  };

  // Mapping row handlers
  const handleRowChange = (idx, key, val) => {
    const updated = [...mappingRows];
    const prevRow = normaliseRow(updated[idx]);
    const nextRow = { ...prevRow, [key]: val };

    // Picking a subform/list Zoho field: stash the subform module api_name
    // and prime the sub-field cache. Switching away from a subform clears
    // any nested rows so they don't silently leak into the saved config.
    if (key === "Zoho") {
      const zf = zohoFields.find((f) => (f.api_name || f) === val);
      if (isSubformLikeZohoField(zf)) {
        const ctx = getSubformContext(zf);
        nextRow.subformModuleApiName = ctx?.moduleApiName || "";
        ensureSubformFieldsLoaded(zf);
      } else {
        nextRow.subformModuleApiName = "";
        nextRow.subRows = [];
      }
      // Strict re-check: if the existing Shopify selection no longer
      // type-checks against the new Zoho field, drop it so the user is
      // forced to re-pick a compatible field.
      if (!nextRow.default && nextRow.Shopify && val) {
        const sf = shopifyFields.find(
          (f) => (f.api_name || f) === nextRow.Shopify
        );
        if (sf && !isPairCompatible(zf, sf)) {
          nextRow.Shopify = "";
        }
      }
    }
    // Picking a non-array Shopify field invalidates any nested mapping that
    // existed when this row was a subform↔array pair.
    if (key === "Shopify") {
      const sf = shopifyFields.find((f) => (f.api_name || f) === val);
      if (sf?.type !== "array") {
        nextRow.subRows = [];
      }
      // Strict re-check on the Zoho side.
      if (!nextRow.default && nextRow.Zoho && val) {
        const zf = zohoFields.find((f) => (f.api_name || f) === nextRow.Zoho);
        if (zf && sf && !isPairCompatible(zf, sf)) {
          nextRow.Zoho = "";
          nextRow.subformModuleApiName = "";
          nextRow.subRows = [];
        }
      }
    }

    updated[idx] = nextRow;
    setMappingRows(updated);
  };
  const handleSourceChange = (idx, isDefault) => {
    const updated = [...mappingRows];
    // Clear Shopify slot when switching modes so a stale value isn't reinterpreted.
    // Default-mode rows can't be subform pairings, so drop sub-rows too.
    updated[idx] = {
      ...normaliseRow(updated[idx]),
      default: isDefault === true,
      Shopify: "",
      subRows: isDefault ? [] : updated[idx].subRows || [],
    };
    setMappingRows(updated);
  };
  const addRow = () => setMappingRows([...mappingRows, EMPTY_ROW()]);
  const removeRow = (idx) => {
    if (mappingRows.length <= 1) return;
    setMappingRows(mappingRows.filter((_, i) => i !== idx));
  };

  // Sub-row handlers (operate on the `subRows` array of a parent row)
  const handleSubRowChange = (parentIdx, subIdx, key, val) => {
    setMappingRows((prev) => {
      const next = [...prev];
      const parent = normaliseRow(next[parentIdx]);
      const subs = parent.subRows?.length ? [...parent.subRows] : [EMPTY_ROW()];
      const prevSub = normaliseRow(subs[subIdx]);
      const nextSub = { ...prevSub, [key]: val };

      // Strict re-check at the sub-row level. Sub-row Zoho candidates come
      // from either the grouped zohoFields list (new POST endpoint) or the
      // legacy subformFieldsCache; sub-row Shopify candidates are the dotted
      // "<parent>[].*" fields filtered out of the full Shopify list.
      const groupedSubFieldsForParent = getSubformSubFieldsFromGrouped(
        zohoFields,
        parent.Zoho
      );
      const subZohoFields =
        groupedSubFieldsForParent.length > 0
          ? groupedSubFieldsForParent
          : subformFieldsCache[parent.Zoho]?.fields || [];
      const subShopifyFields = filterShopifyArraySubFields(
        shopifyFields,
        parent.Shopify
      );
      if (key === "Zoho" && nextSub.Shopify && val) {
        const zf = subZohoFields.find((f) => (f.api_name || f) === val);
        const sf = subShopifyFields.find(
          (f) => (f.api_name || f) === nextSub.Shopify
        );
        if (zf && sf && !isPairCompatible(zf, sf)) nextSub.Shopify = "";
      }
      if (key === "Shopify" && nextSub.Zoho && val) {
        const zf = subZohoFields.find(
          (f) => (f.api_name || f) === nextSub.Zoho
        );
        const sf = subShopifyFields.find((f) => (f.api_name || f) === val);
        if (zf && sf && !isPairCompatible(zf, sf)) nextSub.Zoho = "";
      }

      subs[subIdx] = nextSub;
      next[parentIdx] = { ...parent, subRows: subs };
      return next;
    });
  };
  const addSubRow = (parentIdx) => {
    setMappingRows((prev) => {
      const next = [...prev];
      const parent = normaliseRow(next[parentIdx]);
      const subs = parent.subRows?.length ? [...parent.subRows] : [EMPTY_ROW()];
      next[parentIdx] = { ...parent, subRows: [...subs, EMPTY_ROW()] };
      return next;
    });
  };
  const removeSubRow = (parentIdx, subIdx) => {
    setMappingRows((prev) => {
      const next = [...prev];
      const parent = normaliseRow(next[parentIdx]);
      const subs = parent.subRows || [];
      if (subs.length <= 1) {
        // Always keep at least one editable sub-row so the user can re-pick.
        next[parentIdx] = { ...parent, subRows: [EMPTY_ROW()] };
      } else {
        next[parentIdx] = {
          ...parent,
          subRows: subs.filter((_, i) => i !== subIdx),
        };
      }
      return next;
    });
  };

  const STEPS = ["Shopify", "Zoho App", "Field Mapping", "Actions"];

  // Unique filter option values derived from current configs.
  const serviceOptions = useMemo(
    () =>
      Array.from(new Set(configs.map((c) => c.service).filter(Boolean))).sort(),
    [configs]
  );
  const moduleOptions = useMemo(
    () =>
      Array.from(new Set(configs.map((c) => c.module).filter(Boolean))).sort(),
    [configs]
  );
  const shopOptions = useMemo(
    () =>
      Array.from(new Set(configs.map((c) => c.shop).filter(Boolean))).sort(),
    [configs]
  );

  // Apply filters + sort for the table view.
  const displayedConfigs = useMemo(() => {
    const needle = searchText.trim().toLowerCase();
    const filtered = configs.filter((c) => {
      if (serviceFilter !== "all" && c.service !== serviceFilter) return false;
      if (moduleFilter !== "all" && c.module !== moduleFilter) return false;
      if (shopFilter !== "all" && c.shop !== shopFilter) return false;
      if (statusFilter === "enabled" && c.enabled === false) return false;
      if (statusFilter === "disabled" && c.enabled !== false) return false;
      if (needle) {
        const haystack = [
          c.trigger,
          c.module,
          c.service,
          c.shop,
          c.recordAction,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        if (!haystack.includes(needle)) return false;
      }
      return true;
    });

    const getSortValue = (cfg) => {
      switch (sortBy) {
        case "enabled":
          return cfg.enabled === false ? 0 : 1;
        case "fieldCount":
          return cfg.rows?.length || 0;
        case "trigger":
          return cfg.trigger || "";
        case "module":
          return cfg.module || "";
        case "service":
          return cfg.service || "";
        case "shop":
          return cfg.shop || "";
        case "recordAction":
          return cfg.recordAction || "";
        default:
          return "";
      }
    };

    const sorted = [...filtered].sort((a, b) => {
      const cmp = compareValues(getSortValue(a), getSortValue(b));
      return sortDir === "asc" ? cmp : -cmp;
    });
    return sorted;
  }, [
    configs,
    searchText,
    serviceFilter,
    moduleFilter,
    shopFilter,
    statusFilter,
    sortBy,
    sortDir,
  ]);

  const activeFilterCount =
    (serviceFilter !== "all" ? 1 : 0) +
    (moduleFilter !== "all" ? 1 : 0) +
    (shopFilter !== "all" ? 1 : 0) +
    (statusFilter !== "all" ? 1 : 0) +
    (searchText ? 1 : 0);

  const clearFilters = () => {
    setSearchText("");
    setServiceFilter("all");
    setModuleFilter("all");
    setShopFilter("all");
    setStatusFilter("all");
  };

  return (
    <Box>
      {!embedded && (
      <>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          mb: 2.5,
        }}
      >
        <Box>
          <Typography
            sx={{
              fontFamily: FONT_HEADING,
              fontSize: 18,
              fontWeight: 600,
              mb: "4px",
              letterSpacing: "-0.02em",
            }}
          >
            Sync Rules
          </Typography>
          <Typography sx={{ fontSize: 14, color: "#71717a" }}>
            Predefined sync configurations between Shopify and Zoho CRM
          </Typography>
        </Box>
        <Button
          variant="contained"
          onClick={() => openModal(null)}
          sx={{
            textTransform: "none",
            fontWeight: 600,
            bgcolor: "#32373c",
            "&:hover": { bgcolor: "#3d4349" },
          }}
        >
          + Add Mapping
        </Button>
      </Box>

      {/* Filter Bar */}
      {!configsLoading && configs.length > 0 && (
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 1.25,
            alignItems: "center",
            mb: 1.5,
            p: 1.5,
            bgcolor: "#fff",
            border: "1px solid #e4e4e7",
            borderRadius: "8px",
          }}
        >
          <TextField
            size="small"
            placeholder="Search trigger, module, shop..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            sx={{
              minWidth: 240,
              flex: "1 1 240px",
              "& .MuiInputBase-input": { fontSize: 14 },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 17, color: "#a1a1aa" }} />
                </InputAdornment>
              ),
            }}
          />
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel sx={{ fontSize: 14 }}>Service</InputLabel>
            <Select
              label="Service"
              value={serviceFilter}
              onChange={(e) => setServiceFilter(e.target.value)}
              sx={{ fontSize: 14 }}
            >
              <MenuItem value="all" sx={{ fontSize: 14 }}>
                All services
              </MenuItem>
              {serviceOptions.map((s) => (
                <MenuItem key={s} value={s} sx={{ fontSize: 14 }}>
                  {s}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel sx={{ fontSize: 14 }}>Module</InputLabel>
            <Select
              label="Module"
              value={moduleFilter}
              onChange={(e) => setModuleFilter(e.target.value)}
              sx={{ fontSize: 14 }}
            >
              <MenuItem value="all" sx={{ fontSize: 14 }}>
                All modules
              </MenuItem>
              {moduleOptions.map((m) => (
                <MenuItem key={m} value={m} sx={{ fontSize: 14 }}>
                  {m}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel sx={{ fontSize: 14 }}>Shop</InputLabel>
            <Select
              label="Shop"
              value={shopFilter}
              onChange={(e) => setShopFilter(e.target.value)}
              sx={{ fontSize: 14 }}
            >
              <MenuItem value="all" sx={{ fontSize: 14 }}>
                All shops
              </MenuItem>
              {shopOptions.map((sh) => (
                <MenuItem key={sh} value={sh} sx={{ fontSize: 14 }}>
                  {sh}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 130 }}>
            <InputLabel sx={{ fontSize: 14 }}>Status</InputLabel>
            <Select
              label="Status"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              sx={{ fontSize: 14 }}
            >
              <MenuItem value="all" sx={{ fontSize: 14 }}>
                All
              </MenuItem>
              <MenuItem value="enabled" sx={{ fontSize: 14 }}>
                Enabled
              </MenuItem>
              <MenuItem value="disabled" sx={{ fontSize: 14 }}>
                Disabled
              </MenuItem>
            </Select>
          </FormControl>
          {activeFilterCount > 0 && (
            <Button
              size="small"
              onClick={clearFilters}
              sx={{ textTransform: "none", fontSize: 13, color: "#71717a" }}
            >
              Clear ({activeFilterCount})
            </Button>
          )}
          <Box sx={{ ml: "auto", fontSize: 13, color: "#71717a" }}>
            {displayedConfigs.length} of {configs.length}
          </Box>
        </Box>
      )}

      {/* Config Table or Empty State */}
      {configsLoading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            py: 6,
          }}
        >
          <CircularProgress size={24} sx={{ color: "#71717a" }} />
        </Box>
      ) : configs.length === 0 ? (
        <Box sx={{ textAlign: "center", py: 6, color: "#71717a" }}>
          <Typography sx={{ fontSize: 40, mb: 1.5 }}>&#x1F5FA;</Typography>
          <Typography
            sx={{ fontSize: 16, fontWeight: 600, color: "#09090b", mb: "6px" }}
          >
            No field mappings yet
          </Typography>
          <Typography sx={{ fontSize: 14 }}>
            Click "+ Add Mapping" to create your first sync configuration.
          </Typography>
        </Box>
      ) : (
        <TableContainer
          sx={{
            bgcolor: "#fff",
            border: "1px solid #e4e4e7",
            borderRadius: "8px",
            overflow: "hidden",
          }}
        >
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                {TABLE_COLUMNS.map((col) => (
                  <TableCell
                    key={col.key}
                    align={col.align || "left"}
                    sortDirection={sortBy === col.key ? sortDir : false}
                    sx={{
                      fontSize: 13,
                      fontWeight: 600,
                      color: "#71717a",
                      bgcolor: "#fafafa",
                      textTransform: "uppercase",
                      letterSpacing: "0.04em",
                      borderColor: "#e4e4e7",
                      py: 1.2,
                      width: col.width,
                    }}
                  >
                    {col.sortable ? (
                      <TableSortLabel
                        active={sortBy === col.key}
                        direction={sortBy === col.key ? sortDir : "asc"}
                        onClick={() => handleSort(col.key)}
                      >
                        {col.label}
                      </TableSortLabel>
                    ) : (
                      col.label
                    )}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {displayedConfigs.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={TABLE_COLUMNS.length}
                    sx={{
                      textAlign: "center",
                      py: 5,
                      color: "#a1a1aa",
                      fontSize: 14,
                      borderBottom: 0,
                    }}
                  >
                    No mappings match the current filters.
                  </TableCell>
                </TableRow>
              ) : (
                displayedConfigs.map((cfg) => {
                  const serviceChip = SERVICE_CHIP_COLORS[cfg.service] || {
                    bg: "#f4f4f5",
                    color: "#71717a",
                  };
                  const isEnabled = cfg.enabled !== false;
                  return (
                    <TableRow
                      key={cfg.id}
                      hover
                      sx={{ "&:last-child td": { borderBottom: 0 } }}
                    >
                      <TableCell sx={{ py: 0.75, borderColor: "#f4f4f5" }}>
                        <Switch
                          size="small"
                          checked={isEnabled}
                          onChange={(e) =>
                            toggleConfig(cfg.id, e.target.checked)
                          }
                        />
                      </TableCell>
                      <TableCell sx={{ py: 0.75, borderColor: "#f4f4f5" }}>
                        <Typography
                          sx={{
                            fontSize: 14,
                            fontWeight: 500,
                            color: "#09090b",
                          }}
                        >
                          {cfg.trigger || "—"}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ py: 0.75, borderColor: "#f4f4f5" }}>
                        <Typography sx={{ fontSize: 14, color: "#3f3f46" }}>
                          {cfg.module || "—"}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ py: 0.75, borderColor: "#f4f4f5" }}>
                        <Chip
                          label={cfg.service || "—"}
                          size="small"
                          sx={{
                            fontSize: 12,
                            height: 22,
                            bgcolor: serviceChip.bg,
                            color: serviceChip.color,
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ py: 0.75, borderColor: "#f4f4f5" }}>
                        <Typography sx={{ fontSize: 13, color: "#71717a" }}>
                          {cfg.shop || "—"}
                        </Typography>
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ py: 0.75, borderColor: "#f4f4f5" }}
                      >
                        <Typography
                          sx={{
                            fontSize: 14,
                            color: "#3f3f46",
                            fontVariantNumeric: "tabular-nums",
                          }}
                        >
                          {cfg.rows?.length || 0}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ py: 0.75, borderColor: "#f4f4f5" }}>
                        {cfg.recordAction ? (
                          (() => {
                            const actionKey = String(
                              cfg.recordAction
                            ).toLowerCase();
                            const palette = ACTION_CHIP_COLORS[actionKey] || {
                              bg: "#f4f4f5",
                              color: "#71717a",
                              border: "#e4e4e7",
                            };
                            return (
                              <Chip
                                label={
                                  actionKey.charAt(0).toUpperCase() +
                                  actionKey.slice(1)
                                }
                                size="small"
                                variant="outlined"
                                sx={{
                                  fontSize: 12,
                                  height: 22,
                                  fontWeight: 600,
                                  bgcolor: palette.bg,
                                  color: palette.color,
                                  borderColor: palette.border,
                                  borderWidth: "1.5px",
                                }}
                              />
                            );
                          })()
                        ) : (
                          <Typography sx={{ fontSize: 13, color: "#a1a1aa" }}>
                            —
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ py: 0.75, borderColor: "#f4f4f5" }}
                      >
                        <Tooltip title="Edit">
                          <IconButton
                            size="small"
                            onClick={() => openModal(cfg.id)}
                            sx={{ color: "#3f3f46" }}
                          >
                            <EditIcon sx={{ fontSize: 17 }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            onClick={() => deleteConfig(cfg.id)}
                            sx={{ color: "#ef4444" }}
                          >
                            <DeleteIcon sx={{ fontSize: 17 }} />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      </>
      )}

      {/* ── 4-Step Wizard Modal ── */}
      <Dialog
        open={modalOpen}
        onClose={closeModal}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "8px",
            maxWidth: 800,
            overflow: "hidden",
            // Cap the dialog height and lay it out as a column so the header,
            // step indicator and footer stay pinned while only the step panel
            // scrolls.
            maxHeight: "90vh",
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        {/* Header */}
        <Box
          sx={{
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 2.5,
            py: 2,
            borderBottom: "1px solid #e4e4e7",
          }}
        >
          <Typography sx={{ fontFamily: FONT_HEADING, fontSize: 16, fontWeight: 600 }}>
            {editingId !== null ? "Edit Field Mapping" : "Add Field Mapping"}
          </Typography>
          <Box
            component="button"
            onClick={closeModal}
            sx={{
              background: "none",
              border: "none",
              cursor: "pointer",
              fontSize: 18,
              color: "#71717a",
              p: "2px 6px",
              borderRadius: "4px",
              "&:hover": { bgcolor: "#f4f4f5" },
            }}
          >
            &#x2715;
          </Box>
        </Box>

        {/* Step Indicator */}
        <Box sx={{ flexShrink: 0 }}>
        <StepIndicator
          currentStep={currentStep}
          steps={STEPS}
          maxReachable={
            shopName &&
            triggerEvent &&
            zohoApp &&
            moduleName &&
            mappingRows.length > 0 &&
            mappingRows.every((r) => r.Shopify && r.Zoho)
              ? 3
              : shopName && triggerEvent && zohoApp && moduleName
              ? 2
              : shopName && triggerEvent
              ? 1
              : 0
          }
          onStepClick={(i) => setCurrentStep(i)}
        />
        </Box>

        {/* Step Panels — the only scrollable region */}
        <Box sx={{ px: 3, pb: 1, minHeight: 280, flex: 1, overflowY: "auto" }}>
          {/* Step 1: Shopify */}
          {currentStep === 0 && (
            <Box>
              <Typography
                sx={{
                  fontSize: 14,
                  fontWeight: 600,
                  mb: 1.75,
                  pb: 1,
                  borderBottom: "1px solid #f4f4f5",
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                &#x1F3EA; Shopify Configuration
              </Typography>
              <Typography sx={{ fontSize: 13, color: "#71717a", mb: 2 }}>
                Select the Shopify store and the event that will trigger the
                sync.
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 1.5,
                  maxWidth: 420,
                }}
              >
                <Box>
                  <Typography
                    sx={{
                      fontSize: 13,
                      fontWeight: 600,
                      color: "#3f3f46",
                      mb: "5px",
                    }}
                  >
                    Sync Direction
                  </Typography>
                  <Autocomplete
                    size="small"
                    fullWidth
                    disabled={!!editingId}
                    disableClearable
                    options={[DIRECTION_SHOPIFY_TO_CRM, DIRECTION_CRM_TO_SHOPIFY]}
                    getOptionLabel={(opt) =>
                      opt === DIRECTION_CRM_TO_SHOPIFY
                        ? "Zoho CRM → Shopify (CRM workflow trigger)"
                        : "Shopify → Zoho CRM (Shopify webhook trigger)"
                    }
                    value={direction}
                    onChange={(_, newVal) => {
                      const next = normaliseDirection(newVal);
                      if (next === direction) return;
                      setDirection(next);
                      // The trigger catalogues don't overlap — clear the pick.
                      setTriggerEvent("");
                    }}
                    renderInput={(params) => <TextField {...params} />}
                  />
                  {!!editingId && (
                    <Typography sx={{ fontSize: 12, color: "#a1a1aa", mt: "3px" }}>
                      Direction is fixed for an existing mapping. Create a new
                      mapping to sync the other way.
                    </Typography>
                  )}
                </Box>
                <Box>
                  <Typography
                    sx={{
                      fontSize: 13,
                      fontWeight: 600,
                      color: "#3f3f46",
                      mb: "5px",
                    }}
                  >
                    Shop Name
                  </Typography>
                  <Autocomplete
                    size="small"
                    fullWidth
                    disabled={storesLoading}
                    options={stores}
                    getOptionLabel={(opt) =>
                      opt?.domain || opt?.name || opt || ""
                    }
                    value={
                      stores.find(
                        (s) => (s.domain || s.name || s) === shopName
                      ) || null
                    }
                    onChange={(_, newVal) =>
                      setShopName(
                        newVal ? newVal.domain || newVal.name || newVal : ""
                      )
                    }
                    loading={storesLoading}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder={
                          storesLoading
                            ? "Loading stores..."
                            : "— Select shop —"
                        }
                      />
                    )}
                    isOptionEqualToValue={(opt, val) =>
                      (opt.domain || opt.name || opt) ===
                      (val.domain || val.name || val)
                    }
                  />
                </Box>
                <Box>
                  {(() => {
                    // Triggers already used by another config for this shop IN
                    // THE SAME DIRECTION. Hidden from the dropdown so the user
                    // can't create a duplicate, but surfaced via a compact chip
                    // + tooltip so they know why a valid-looking event is
                    // missing. The same trigger CAN exist once per direction.
                    // CRM workflow events are per-MODULE (the module is picked
                    // in step 2), so nothing is hidden here for CRM2Shopify —
                    // uniqueness is enforced at the module step instead.
                    const usedTriggers =
                      shopName && !isCrmToShopify
                        ? configs
                            .filter(
                              (c) =>
                                c.shop === shopName &&
                                c.id !== editingId &&
                                normaliseDirection(c.direction) === direction
                            )
                            .map((c) => c.trigger)
                        : [];
                    const hiddenTriggers = TRIGGER_OPTIONS.filter((w) =>
                      usedTriggers.includes(w.value)
                    );
                    const hasHidden = hiddenTriggers.length > 0;
                    const options = TRIGGER_OPTIONS.filter(
                      (w) => !usedTriggers.includes(w.value)
                    );
                    return (
                      <>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            mb: "5px",
                            minHeight: 18,
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: 13,
                              fontWeight: 600,
                              color: "#3f3f46",
                            }}
                          >
                            Trigger Event
                          </Typography>
                          {hasHidden && (
                            <Tooltip
                              arrow
                              placement="top-end"
                              title={
                                <Box
                                  sx={{
                                    fontSize: 13,
                                    lineHeight: 1.5,
                                    maxWidth: 240,
                                  }}
                                >
                                  <Box sx={{ fontWeight: 600, mb: "2px" }}>
                                    {hiddenTriggers.length} trigger
                                    {hiddenTriggers.length !== 1 ? "s" : ""}{" "}
                                    hidden
                                  </Box>
                                  Already used by other settings for this shop.
                                  Check disabled or previous settings, or type
                                  in the search bar to locate them.
                                </Box>
                              }
                            >
                              <Box
                                sx={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: "4px",
                                  px: "6px",
                                  py: "1px",
                                  bgcolor: "#fffbeb",
                                  border: "1px solid #fde68a",
                                  borderRadius: "10px",
                                  fontSize: 12,
                                  fontWeight: 600,
                                  color: "#92400e",
                                  cursor: "help",
                                  lineHeight: 1.4,
                                }}
                              >
                                <Box component="span" sx={{ fontSize: 12 }}>
                                  &#9888;
                                </Box>
                                {hiddenTriggers.length} hidden
                              </Box>
                            </Tooltip>
                          )}
                        </Box>
                        <Autocomplete
                          size="small"
                          fullWidth
                          options={options}
                          groupBy={(opt) => opt.category}
                          getOptionLabel={(opt) => opt?.label || ""}
                          value={
                            TRIGGER_OPTIONS.find(
                              (w) => w.value === triggerEvent
                            ) || null
                          }
                          onChange={(_, newVal) =>
                            setTriggerEvent(newVal ? newVal.value : "")
                          }
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              placeholder="— Select trigger —"
                            />
                          )}
                          isOptionEqualToValue={(opt, val) =>
                            opt.value === val.value
                          }
                          noOptionsText={
                            <Box
                              sx={{
                                fontSize: 13,
                                color: "#71717a",
                                lineHeight: 1.5,
                              }}
                            >
                              <Box
                                sx={{
                                  fontWeight: 600,
                                  color: "#09090b",
                                  mb: "2px",
                                }}
                              >
                                No matching trigger events
                              </Box>
                              {hasHidden ? (
                                <>
                                  Please check disabled or previous settings to
                                  see if this trigger event was already created.
                                  <Box sx={{ mt: "4px", color: "#a1a1aa" }}>
                                    Try clearing your search, or look for it in
                                    the Field Mapping list (including disabled
                                    rows).
                                  </Box>
                                </>
                              ) : (
                                <>
                                  Try typing a different keyword in the search.
                                </>
                              )}
                            </Box>
                          }
                        />
                      </>
                    );
                  })()}
                </Box>
              </Box>
            </Box>
          )}

          {/* Step 2: Zoho App */}
          {currentStep === 1 && (
            <Box>
              <Typography
                sx={{
                  fontSize: 14,
                  fontWeight: 600,
                  mb: 1.75,
                  pb: 1,
                  borderBottom: "1px solid #f4f4f5",
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                &#x1F517; Zoho App Configuration
              </Typography>
              <Typography sx={{ fontSize: 13, color: "#71717a", mb: 2 }}>
                Select the authenticated Zoho app and the target module where
                data will be synced.
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 1.5,
                  maxWidth: 420,
                }}
              >
                <Box>
                  <Typography
                    sx={{
                      fontSize: 13,
                      fontWeight: 600,
                      color: "#3f3f46",
                      mb: "5px",
                    }}
                  >
                    Zoho App
                  </Typography>
                  <Autocomplete
                    size="small"
                    fullWidth
                    // Zoho App is fixed to Zoho CRM in this build. The picker
                    // stays visible (for context) but is non-interactive.
                    disabled
                    readOnly
                    options={ZOHO_APPS}
                    getOptionLabel={(opt) => opt?.label || ""}
                    value={ZOHO_APPS.find((a) => a.label === zohoApp) || null}
                    renderInput={(params) => (
                      <TextField {...params} placeholder="Zoho CRM" />
                    )}
                    isOptionEqualToValue={(opt, val) => opt.key === val.key}
                  />
                </Box>
                <Box>
                  <Typography
                    sx={{
                      fontSize: 13,
                      fontWeight: 600,
                      color: "#3f3f46",
                      mb: "5px",
                    }}
                  >
                    Module Name
                  </Typography>
                  <Autocomplete
                    size="small"
                    fullWidth
                    disabled={modulesLoading}
                    options={zohoModules}
                    getOptionLabel={(opt) =>
                      opt?.plural_label || opt?.module_name || opt || ""
                    }
                    value={
                      zohoModules.find(
                        (m) => (m.api_name || m) === moduleName
                      ) || null
                    }
                    onChange={(_, newVal) =>
                      handleModuleChange(newVal ? newVal.api_name || newVal : "")
                    }
                    loading={modulesLoading}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder={
                          modulesLoading
                            ? "Loading modules..."
                            : "— Select module —"
                        }
                      />
                    )}
                    isOptionEqualToValue={(opt, val) =>
                      (opt.api_name || opt) === (val.api_name || val)
                    }
                  />
                </Box>
              </Box>
            </Box>
          )}

          {/* Step 3: Field Mapping */}
          {currentStep === 2 && (
            <Box>
              {/* Summary of Step 1 & 2 */}
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2 }}>
                <Box
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "6px",
                    fontSize: 13,
                    color: "#3f3f46",
                    bgcolor: "#f4f4f5",
                    border: "1px solid #e4e4e7",
                    borderRadius: "6px",
                    px: "10px",
                    py: "5px",
                  }}
                >
                  <Box component="span" sx={{ fontWeight: 600 }}>
                    Shop:
                  </Box>{" "}
                  {shopName}
                </Box>
                <Box
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "6px",
                    fontSize: 13,
                    color: "#3f3f46",
                    bgcolor: "#f4f4f5",
                    border: "1px solid #e4e4e7",
                    borderRadius: "6px",
                    px: "10px",
                    py: "5px",
                  }}
                >
                  <Box component="span" sx={{ fontWeight: 600 }}>
                    Trigger:
                  </Box>{" "}
                  {TRIGGER_OPTIONS.find((w) => w.value === triggerEvent)?.label ||
                    triggerEvent}
                </Box>
                <Box
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "6px",
                    fontSize: 13,
                    color: "#3f3f46",
                    bgcolor: "#f4f4f5",
                    border: "1px solid #e4e4e7",
                    borderRadius: "6px",
                    px: "10px",
                    py: "5px",
                  }}
                >
                  <Box component="span" sx={{ fontWeight: 600 }}>
                    App:
                  </Box>{" "}
                  {zohoApp}
                </Box>
                <Box
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "6px",
                    fontSize: 13,
                    color: "#3f3f46",
                    bgcolor: "#f4f4f5",
                    border: "1px solid #e4e4e7",
                    borderRadius: "6px",
                    px: "10px",
                    py: "5px",
                  }}
                >
                  <Box component="span" sx={{ fontWeight: 600 }}>
                    Module:
                  </Box>{" "}
                  {zohoModules.find((m) => (m.api_name || m) === moduleName)
                    ?.plural_label || moduleName}
                </Box>
              </Box>
              {fieldsLoading ? (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    py: 4,
                    justifyContent: "center",
                  }}
                >
                  <CircularProgress size={20} />
                  <Typography sx={{ fontSize: 14, color: "#71717a" }}>
                    Loading fields...
                  </Typography>
                </Box>
              ) : (
                <>
                  {/* Column headers */}
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "1fr 72px 28px 1fr 64px",
                      alignItems: "center",
                      bgcolor: "#f4f4f5",
                      borderTop: "1px solid #e4e4e7",
                      borderBottom: "1px solid #e4e4e7",
                      px: 0,
                      py: 1,
                      borderRadius: "4px",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: 13,
                        fontWeight: 600,
                        color: "#52525b",
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                        pl: 1.5,
                      }}
                    >
                      <Box
                        sx={{
                          width: 22,
                          height: 22,
                          borderRadius: "50%",
                          bgcolor: "#e0e7ff",
                          color: "#4338ca",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: 11,
                          fontWeight: 700,
                        }}
                      >
                        &#x1F517;
                      </Box>
                      Zoho CRM Fields
                    </Typography>
                    <Box />
                    <Box />
                    <Typography
                      sx={{
                        fontSize: 13,
                        fontWeight: 600,
                        color: "#52525b",
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                      }}
                    >
                      <Box
                        sx={{
                          width: 22,
                          height: 22,
                          borderRadius: "50%",
                          bgcolor: "#16a34a",
                          color: "#fff",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: 13,
                          fontWeight: 700,
                        }}
                      >
                        S
                      </Box>
                      Shopify Fields / Default
                    </Typography>
                    <Box />
                  </Box>
                  <Box sx={{ px: "4px", py: "4px" }}>
                    {mappingRows.map((row, i) => {
                      const zohoField = zohoFields.find(
                        (f) => (f.api_name || f) === row.Zoho
                      );
                      const shopifyField = shopifyFields.find(
                        (f) => (f.api_name || f) === row.Shopify
                      );
                      const isLocked =
                        row.locked === true ||
                        isExtensionOwnedZohoField(row.Zoho) ||
                        isAlwaysLockedDefault(row.Zoho);
                      // Show the nested block as soon as EITHER side resolves
                      // to a container-like field (Zoho subform/list OR
                      // Shopify array). The block guides the user to fill in
                      // the missing side via its empty-state message.
                      const isSubformPair =
                        !row.default &&
                        (isSubformLikeZohoField(zohoField) ||
                          shopifyField?.type === "array");
                      const subCacheEntry = subformFieldsCache[row.Zoho];
                      // Prefer subform sub-fields surfaced under the parent's
                      // group key in the flat zohoFields list (new POST
                      // endpoint shape). Falls back to the cache (legacy /
                      // hardcoded path) when the grouped data isn't there.
                      const groupedSubFields = getSubformSubFieldsFromGrouped(
                        zohoFields,
                        row.Zoho
                      );
                      const renderedSubZohoFields =
                        groupedSubFields.length > 0
                          ? groupedSubFields
                          : subCacheEntry?.fields || [];
                      return (
                        <React.Fragment key={i}>
                          <MappingRow
                            row={row}
                            index={i}
                            shopifyFields={topLevelShopifyFields}
                            zohoFields={zohoFields}
                            allRows={mappingRows}
                            onChange={handleRowChange}
                            onSourceChange={handleSourceChange}
                            onRemove={removeRow}
                            onAdd={addRow}
                            isRequired={!!zohoField?.system_mandatory}
                            isLocked={isLocked}
                            reverse={isCrmToShopify}
                          />
                          {isSubformPair && (
                            <SubformBlock
                              parentRow={row}
                              parentIndex={i}
                              shopifyFields={shopifyFields}
                              subZohoFields={renderedSubZohoFields}
                              subformLoading={!!subCacheEntry?.loading}
                              onSubRowChange={handleSubRowChange}
                              onSubRowAdd={addSubRow}
                              onSubRowRemove={removeSubRow}
                              reverse={isCrmToShopify}
                            />
                          )}
                        </React.Fragment>
                      );
                    })}
                  </Box>
                </>
              )}
            </Box>
          )}

          {/* Step 4: Actions */}
          {currentStep === 3 && (
            <Box>
              <Typography
                sx={{
                  fontSize: 14,
                  fontWeight: 600,
                  mb: 1.75,
                  pb: 1,
                  borderBottom: "1px solid #f4f4f5",
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                &#x26A1; Actions &amp; Options
              </Typography>
              <Typography sx={{ fontSize: 13, color: "#71717a", mb: 2 }}>
                Choose how matching records are handled in Zoho CRM.
              </Typography>
              <RadioGroup
                value={recordAction}
                onChange={(e) => setRecordAction(e.target.value)}
              >
                <FormControlLabel
                  value="create"
                  control={<Radio size="small" />}
                  label={
                    <Typography sx={{ fontSize: 14, fontWeight: 500 }}>
                      Create{" "}
                      <Typography
                        component="span"
                        sx={{ fontSize: 12, color: "#a1a1aa" }}
                      >
                        — Always insert a new record
                      </Typography>
                    </Typography>
                  }
                />
                <FormControlLabel
                  value="upsert"
                  control={<Radio size="small" />}
                  label={
                    <Typography sx={{ fontSize: 14, fontWeight: 500 }}>
                      Upsert{" "}
                      <Typography
                        component="span"
                        sx={{ fontSize: 12, color: "#a1a1aa" }}
                      >
                        — Update if exists, create if not
                      </Typography>
                    </Typography>
                  }
                />
                {recordAction === "upsert" && (
                  <Box sx={{ ml: "23px", mt: 0.5, mb: 0.5 }}>
                    <Typography
                      sx={{
                        fontSize: 13,
                        fontWeight: 600,
                        color: "#3f3f46",
                        mb: "5px",
                      }}
                    >
                      Unique Match Field
                    </Typography>
                    <Autocomplete
                      size="small"
                      sx={{ maxWidth: 260 }}
                      options={zohoFields.filter(
                        (f) =>
                          (f.unique &&
                            typeof f.unique === "object" &&
                            Object.keys(f.unique).length > 0) ||
                          f.unique === true
                      )}
                      getOptionLabel={(opt) =>
                        opt?.display_label || opt?.field_label || opt || ""
                      }
                      value={
                        zohoFields.find(
                          (f) => (f.api_name || f) === upsertField
                        ) || null
                      }
                      onChange={(_, newVal) =>
                        setUpsertField(newVal ? newVal.api_name || newVal : "")
                      }
                      renderInput={(params) => (
                        <TextField {...params} placeholder="— Select field —" />
                      )}
                      isOptionEqualToValue={(opt, val) =>
                        (opt.api_name || opt) === (val.api_name || val)
                      }
                    />
                  </Box>
                )}
                <FormControlLabel
                  value="update"
                  control={<Radio size="small" />}
                  label={
                    <Typography sx={{ fontSize: 14, fontWeight: 500 }}>
                      Update Only{" "}
                      <Typography
                        component="span"
                        sx={{ fontSize: 12, color: "#a1a1aa" }}
                      >
                        — Skip if record doesn't exist
                      </Typography>
                    </Typography>
                  }
                />
                {recordAction === "update" && (
                  <Box sx={{ ml: "23px", mt: 0.5, mb: 0.5 }}>
                    <Typography
                      sx={{
                        fontSize: 13,
                        fontWeight: 600,
                        color: "#3f3f46",
                        mb: "5px",
                      }}
                    >
                      Unique Match Field
                    </Typography>
                    <Autocomplete
                      size="small"
                      sx={{ maxWidth: 260 }}
                      options={zohoFields.filter(
                        (f) =>
                          (f.unique &&
                            typeof f.unique === "object" &&
                            Object.keys(f.unique).length > 0) ||
                          f.unique === true
                      )}
                      getOptionLabel={(opt) =>
                        opt?.display_label || opt?.field_label || opt || ""
                      }
                      value={
                        zohoFields.find(
                          (f) => (f.api_name || f) === upsertField
                        ) || null
                      }
                      onChange={(_, newVal) =>
                        setUpsertField(newVal ? newVal.api_name || newVal : "")
                      }
                      renderInput={(params) => (
                        <TextField {...params} placeholder="— Select field —" />
                      )}
                      isOptionEqualToValue={(opt, val) =>
                        (opt.api_name || opt) === (val.api_name || val)
                      }
                    />
                  </Box>
                )}
                <FormControlLabel
                  value="delete"
                  control={<Radio size="small" />}
                  label={
                    <Typography sx={{ fontSize: 14, fontWeight: 500 }}>
                      Delete{" "}
                      <Typography
                        component="span"
                        sx={{ fontSize: 12, color: "#a1a1aa" }}
                      >
                        — Remove the matching record
                      </Typography>
                    </Typography>
                  }
                />
                {recordAction === "delete" && (
                  <Box sx={{ ml: "23px", mt: 0.5, mb: 0.5 }}>
                    <Typography
                      sx={{
                        fontSize: 13,
                        fontWeight: 600,
                        color: "#3f3f46",
                        mb: "5px",
                      }}
                    >
                      Match Field
                    </Typography>
                    <TextField
                      size="small"
                      value={shopifyIdFieldForCurrent || "Shopify ID"}
                      disabled
                      sx={{
                        maxWidth: 320,
                        "& .MuiInputBase-input": {
                          fontSize: 14,
                          fontFamily: "monospace",
                        },
                      }}
                    />
                    <Typography
                      sx={{ fontSize: 12, color: "#a1a1aa", mt: "4px" }}
                    >
                      Delete actions always match by the extension-owned Shopify
                      ID field.
                    </Typography>
                  </Box>
                )}
              </RadioGroup>
            </Box>
          )}
        </Box>

        {/* Footer */}
        <Box
          sx={{
            flexShrink: 0,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            px: 2.5,
            py: 1.75,
            borderTop: "1px solid #e4e4e7",
            bgcolor: "#fff",
          }}
        >
          <Button
            variant="outlined"
            size="small"
            onClick={closeModal}
            sx={{
              textTransform: "none",
              color: "#3f3f46",
              borderColor: "#e4e4e7",
            }}
          >
            Cancel
          </Button>
          <Box sx={{ display: "flex", gap: 1 }}>
            {currentStep > 0 && (
              <Button
                variant="outlined"
                size="small"
                onClick={prevStep}
                sx={{
                  textTransform: "none",
                  color: "#3f3f46",
                  borderColor: "#e4e4e7",
                }}
              >
                &larr; Back
              </Button>
            )}
            <Button
              variant="contained"
              size="small"
              onClick={nextStep}
              disabled={
                savingMapping ||
                (currentStep === 0 && (!shopName || !triggerEvent)) ||
                (currentStep === 1 && (!zohoApp || !moduleName)) ||
                (currentStep === 2 &&
                  mappingRows.some((r) => !r.Shopify || !r.Zoho)) ||
                (currentStep === 3 && recordAction !== "create" && !upsertField)
              }
              startIcon={
                savingMapping && currentStep === 3 ? (
                  <CircularProgress size={14} sx={{ color: "#fff" }} />
                ) : null
              }
              sx={{
                textTransform: "none",
                fontWeight: 600,
                bgcolor: "#32373c",
                "&:hover": { bgcolor: "#3d4349" },
                "&.Mui-disabled": { bgcolor: "#d4d4d8", color: "#a1a1aa" },
              }}
            >
              {currentStep === 3
                ? savingMapping
                  ? "Saving\u2026"
                  : "Save Mapping"
                : "Next \u2192"}
            </Button>
          </Box>
        </Box>
      </Dialog>
    </Box>
  );
}
